/* RK Product Customiser. Inline PDP section.
 * All classes prefixed `rk-pc-` so they don't collide with the theme's CSS.
 * Brand tokens (pink/plum/cream, Nuckle headlines) inherited from the host
 * page when present; safe fallbacks defined here as :host-level CSS vars on
 * the root container so the section looks right even on minimal themes. */

.rk-pc-root,
.rk-pc-mobile-bar,
.rk-pc-lead-overlay {
  /* Local brand tokens used as fallbacks when the theme hasn't set its
   * own. Theme CSS vars (--rk-pink, --rk-plum, etc.) override these
   * cleanly via the cascade. Also declared on the mobile bar + lead
   * overlay: both PORTAL to document.body (2026-07-24 stacking fix), so
   * they no longer inherit from the root. */
  --rk-pc-pink: #f38bb9;
  --rk-pc-pink-soft: #fcdaf4;
  --rk-pc-pink-pale: #fdf2f8;
  --rk-pc-plum: #5c1756;
  --rk-pc-plum-soft: #7e3877;
  --rk-pc-plum-pale: #e9dafd;
  --rk-pc-amber: #f1c672;
  --rk-pc-amber-ink: #7a4a0f;
  --rk-pc-mint-soft: #d3efb7;
  --rk-pc-forest: #6ca085;
  --rk-pc-ink: #111111;
  --rk-pc-ink-2: #374151;
  --rk-pc-muted: #6b7280;
  --rk-pc-hairline: #e5e7eb;
  --rk-pc-hairline-soft: #f3f4f6;
  --rk-pc-card: #ffffff;

  --rk-pc-r-sm: 6px;
  --rk-pc-r-md: 10px;
  --rk-pc-r-lg: 14px;
  --rk-pc-r-xl: 16px;
  --rk-pc-r-pill: 999px;

  --rk-pc-sh-card: 0 1px 3px rgba(17, 17, 17, 0.06);

  /* Visual preview dock: how far below the top of the viewport the
   * preview pins. Bump this if it tucks under the theme's sticky
   * header. */
  --rk-pc-pv-top: 180px;

  --rk-pc-font-display: "Nuckle", "Hanken Grotesk", "Arial Black", system-ui, sans-serif;
  --rk-pc-font-body: "Helvetica Neue", Helvetica, Arial, system-ui, sans-serif;

}

.rk-pc-root {
  font-family: var(--rk-pc-font-body);
  color: var(--rk-pc-ink);
  display: block;
  width: 100%;
  margin: 24px 0;
  /* Anchor point for the mounted visual-preview drawer + its own
   * stacking context so the drawer layers cleanly. z-index lifts the
   * whole customiser above sibling theme chrome in the SAME section
   * stacking context - the product gallery's arrow `.controls` sit at
   * z-10 and were painting over the preview panel (2026-07-24). */
  position: relative;
  isolation: isolate;
  z-index: 20;
}

.rk-pc-root *,
.rk-pc-root *::before,
.rk-pc-root *::after {
  box-sizing: border-box;
}

/* ─── Accordion section shell ─────────────────────────────────────────── */

.rk-pc-sec {
  background: var(--rk-pc-card);
  border: 1px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-xl);
  box-shadow: var(--rk-pc-sh-card);
  overflow: hidden;
  margin-bottom: 14px;
  transition: border-color 150ms ease;
}
.rk-pc-sec.is-active {
  border-color: var(--rk-pc-pink);
  box-shadow: 0 6px 18px rgba(243, 139, 185, 0.18);
}

.rk-pc-sec__head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  width: 100%;
  background: none;
  border: 0;
  text-align: left;
  color: inherit;
  cursor: pointer;
  font: inherit;
}
.rk-pc-sec__num {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: var(--rk-pc-hairline-soft);
  /* Design pass: was --rk-pc-muted (#6b7280) on #f3f4f6 ~4.2:1, just
     under AA. ink-2 (#374151) clears it comfortably. */
  color: var(--rk-pc-ink-2);
  font: 700 14px/1 var(--rk-pc-font-display);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 150ms ease, color 150ms ease;
}
.rk-pc-sec.is-active .rk-pc-sec__num {
  background: var(--rk-pc-pink);
  color: var(--rk-pc-plum);
}
/* Completed section: pink badge with a tick, regardless of active state. */
.rk-pc-sec__num.is-done {
  background: var(--rk-pc-pink);
  color: var(--rk-pc-plum);
}
.rk-pc-sec__heading {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.rk-pc-sec__title {
  font: 700 18px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-sec__summary {
  font-size: 13px;
  color: var(--rk-pc-muted);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rk-pc-sec.is-active .rk-pc-sec__summary {
  color: var(--rk-pc-pink);
  font-weight: 600;
}
.rk-pc-sec__price {
  font: 700 14px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
  flex-shrink: 0;
}
.rk-pc-sec__price.is-free {
  color: var(--rk-pc-forest);
}
.rk-pc-sec__chevron {
  flex-shrink: 0;
  color: var(--rk-pc-muted);
  font-size: 13px;
  margin-left: 2px;
}
.rk-pc-sec__body {
  padding: 0 20px 24px;
  border-top: 1px solid var(--rk-pc-hairline-soft);
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding-top: 18px;
}

/* ─── Placement card grid ─────────────────────────────────────────────── */

.rk-pc-pl-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
@media (min-width: 560px) {
  .rk-pc-pl-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

.rk-pc-pl-card {
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  padding: 14px 10px 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  transition: all 150ms ease;
  cursor: pointer;
  font: inherit;
  color: inherit;
}
.rk-pc-pl-card:hover:not(:disabled) {
  border-color: var(--rk-pc-pink);
  transform: translateY(-1px);
}
.rk-pc-pl-card.is-selected {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
  box-shadow: inset 0 0 0 2px var(--rk-pc-pink);
}
.rk-pc-pl-card.is-disabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: var(--rk-pc-hairline-soft);
  border-color: var(--rk-pc-hairline);
}
.rk-pc-pl-card.is-disabled:hover {
  transform: none;
  border-color: var(--rk-pc-hairline);
}
.rk-pc-pl-card__icon {
  width: 38px;
  height: 38px;
  color: var(--rk-pc-plum-soft);
}
.rk-pc-pl-card.is-selected .rk-pc-pl-card__icon {
  color: var(--rk-pc-pink);
}
.rk-pc-pl-card__photo {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--rk-pc-hairline);
  background: var(--rk-pc-card);
}
.rk-pc-pl-card__title {
  font: 700 13px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-pl-card__hint {
  font-size: 11px;
  color: var(--rk-pc-muted);
  line-height: 1.3;
}
.rk-pc-pl-card__max {
  font: 600 11px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-forest);
  margin-top: 2px;
}

/* ─── Content-type toggle (Logo / Text) ───────────────────────────────── */

.rk-pc-mode-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.rk-pc-mode-row--three {
  grid-template-columns: 1fr 1fr 1fr;
}
@media (max-width: 480px) {
  .rk-pc-mode-row--three {
    grid-template-columns: 1fr;
  }
}
.rk-pc-mode-tile {
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  padding: 14px 12px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: all 150ms ease;
}
.rk-pc-mode-tile:hover {
  border-color: var(--rk-pc-pink);
}
.rk-pc-mode-tile.is-selected {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
  box-shadow: inset 0 0 0 2px var(--rk-pc-pink);
}
.rk-pc-mode-tile__title {
  font: 700 15px/1.2 var(--rk-pc-font-display);
}
.rk-pc-mode-tile__desc {
  font-size: 12px;
  color: var(--rk-pc-muted);
}
.rk-pc-mode-tile__price {
  margin-top: 6px;
  font: 700 13px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-plum);
  letter-spacing: 0.02em;
}
/* Phase 17c: replaces-step add-on tiles. Tinted with the upsell's accent
 * colour when one is provided (via the inline CSS variable). */
.rk-pc-addon-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}
.rk-pc-addon-row__head {
  font: 600 11px/1 var(--rk-pc-font-body);
  color: var(--rk-pc-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.rk-pc-mode-tile--upsell {
  position: relative;
  border-color: var(--rk-pc-upsell-accent, var(--rk-pc-hairline));
}
.rk-pc-mode-tile--upsell.is-selected {
  border-color: var(--rk-pc-upsell-accent, var(--rk-pc-pink));
  background: var(--rk-pc-pink-pale);
  box-shadow: inset 0 0 0 2px var(--rk-pc-upsell-accent, var(--rk-pc-pink));
}
.rk-pc-mode-tile--upsell .rk-pc-mode-tile__price {
  color: var(--rk-pc-upsell-accent, var(--rk-pc-plum));
}

.rk-pc-mode-tile__price.is-free {
  color: var(--rk-pc-forest);
}
.rk-pc-mode-tile__badge {
  align-self: flex-start;
  background: var(--rk-pc-mint-soft);
  color: var(--rk-pc-forest);
  font: 700 10px/1 var(--rk-pc-font-display);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 999px;
  margin-bottom: 4px;
}

/* ─── Upload drop zone ────────────────────────────────────────────────── */

.rk-pc-drop {
  position: relative;
  border: 2px dashed var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-lg);
  padding: 28px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  text-align: center;
  background: var(--rk-pc-hairline-soft);
  transition: border-color 150ms ease, background 150ms ease, transform 80ms ease;
  /* Mobile Safari adds a tap delay + grey flash on labels/buttons. */
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
  outline: none;
}
.rk-pc-drop:hover,
.rk-pc-drop.is-over,
.rk-pc-drop:focus-visible {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
}
/* Instant pressed feedback so the click never looks "dead" while the
   file picker is opening (Clarity dead-click trigger). */
.rk-pc-drop:active,
.rk-pc-drop.is-opening {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
  transform: scale(0.995);
}
.rk-pc-drop.is-uploading {
  cursor: progress;
  opacity: 0.85;
}
/* Children must not eat the click on iOS — the container handles it. */
.rk-pc-drop > * {
  pointer-events: none;
}
.rk-pc-drop__title {
  font: 700 15px/1.2 var(--rk-pc-font-display);
}
.rk-pc-drop__sub {
  font-size: 12px;
  color: var(--rk-pc-muted);
}
.rk-pc-drop__filename {
  font: 600 12px/1 var(--rk-pc-font-body);
  color: var(--rk-pc-ink);
}
/* Wave 1: in-flight upload feedback. The staging upload uses fetch (no
   progress events) so the bar is indeterminate - a sweep animation, not
   a percent readout. */
.rk-pc-drop__progress {
  position: relative;
  width: 100%;
  max-width: 220px;
  height: 4px;
  border-radius: 999px;
  background: var(--rk-pc-hairline);
  overflow: hidden;
}
.rk-pc-drop__progress-bar {
  position: absolute;
  top: 0;
  left: -40%;
  width: 40%;
  height: 100%;
  border-radius: 999px;
  background: var(--rk-pc-pink);
  animation: rk-pc-upload-sweep 1.1s ease-in-out infinite;
}
@keyframes rk-pc-upload-sweep {
  0% {
    left: -40%;
  }
  100% {
    left: 100%;
  }
}
/* Wave 1: flashed when the customer re-taps the dropzone mid-upload, so
   the tap visibly responds instead of dying. */
.rk-pc-drop__wait {
  font: 600 12px/1.2 var(--rk-pc-font-body);
  color: var(--rk-pc-plum);
}
.rk-pc-fee-note {
  font-size: 12px;
  color: var(--rk-pc-plum-soft);
  display: flex;
  align-items: center;
  gap: 6px;
}
/* Phase 68: free-digitisation promo treatments. */
.rk-pc-fee-note__was {
  text-decoration: line-through;
  text-decoration-thickness: 1.5px;
  text-decoration-color: var(--rk-pc-plum-soft);
  color: var(--rk-pc-plum-soft);
  opacity: 0.75;
}
.rk-pc-fee-note__free {
  color: var(--rk-pc-pink);
  font-weight: 800;
  letter-spacing: 0.02em;
}

/* Strikethrough + "Free" treatment in the price breakdown rows. */
.rk-pc-breakdown__was {
  display: inline-block;
  margin-right: 6px;
  text-decoration: line-through;
  text-decoration-thickness: 1.5px;
  color: var(--rk-pc-muted);
  opacity: 0.75;
}
.rk-pc-breakdown__free {
  color: var(--rk-pc-pink);
  font-weight: 700;
}

/* Promo banner at the top of the customiser when the
   free-digitisation toggle is on. */
.rk-pc-promo-banner {
  /* Phase 83: now a column of perk rows so multiple offers live in ONE
     banner instead of stacking two separate strips. A single perk
     renders identically to before (one row). */
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 16px;
  margin: 0 0 14px;
  border-radius: var(--rk-pc-r-lg);
  background: linear-gradient(95deg, var(--rk-pc-pink-soft), var(--rk-pc-pink-pale));
  border: 1px solid var(--rk-pc-pink);
  color: var(--rk-pc-ink);
  font: 500 14px/1.35 var(--rk-pc-font-body);
}
.rk-pc-promo-banner__row {
  display: flex;
  align-items: center;
  gap: 10px;
}
/* Two perks: a hairline between rows keeps them distinct without
   looking like two separate banners. */
.rk-pc-promo-banner.is-multi .rk-pc-promo-banner__row + .rk-pc-promo-banner__row {
  padding-top: 10px;
  border-top: 1px solid rgba(243, 139, 185, 0.45);
}
.rk-pc-promo-banner__badge {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--rk-pc-pink);
  color: #fff;
  font: 700 16px/1 var(--rk-pc-font-display);
}
.rk-pc-promo-banner__copy strong {
  font-weight: 800;
}
.rk-pc-promo-banner__save {
  color: var(--rk-pc-pink);
  font-weight: 700;
  white-space: nowrap;
}
@media (max-width: 480px) {
  .rk-pc-promo-banner {
    font-size: 13px;
    padding: 10px 12px;
  }
}

/* ─── Text input + font grid ──────────────────────────────────────────── */

.rk-pc-text-input {
  width: 100%;
  padding: 10px 12px;
  margin-bottom: 16px;
  font: 400 15px/1.4 var(--rk-pc-font-body);
  border: 1px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  background: var(--rk-pc-card);
  color: var(--rk-pc-ink);
}
.rk-pc-text-input:focus {
  outline: 2px solid var(--rk-pc-pink);
  outline-offset: -1px;
  border-color: var(--rk-pc-pink);
}
/* Multi-line variant: vertical resize disabled, fixed 3-row height.
 * Matches the input version visually so the focus ring + padding stay
 * consistent when the customer toggles between single- and multi-line. */
textarea.rk-pc-text-input--multiline {
  resize: none;
  min-height: 3.6em;
  line-height: 1.4;
  font-family: var(--rk-pc-font-body);
}

/* Phase 67b: date-input mode (rk-special-date). Shows the native date
 * picker + a live preview block displaying the Roman numeral string
 * that'll actually stitch. */
.rk-pc-date-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.rk-pc-date-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 14px 16px;
  border-radius: var(--rk-pc-r-md);
  background: var(--rk-pc-cream);
  border: 1px dashed var(--rk-pc-hairline);
}
.rk-pc-date-preview__label {
  font: 500 11px/1 var(--rk-pc-font-body);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--rk-pc-ink-muted);
}
.rk-pc-date-preview strong {
  font: 600 22px/1.2 var(--rk-pc-font-display, var(--rk-pc-font-body));
  letter-spacing: 0.06em;
  color: var(--rk-pc-plum, var(--rk-pc-ink));
}

/* Phase 67c: locked-size info chip (replaces the slider when the
 * template fixes embroidery size to one specific value). */
.rk-pc-locked-size {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: var(--rk-pc-r-md);
  background: var(--rk-pc-cream);
  border: 1px dashed var(--rk-pc-hairline);
}
.rk-pc-locked-size__label {
  font: 500 11px/1 var(--rk-pc-font-body);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--rk-pc-ink-muted);
}
.rk-pc-locked-size strong {
  font: 600 18px/1 var(--rk-pc-font-body);
  color: var(--rk-pc-plum, var(--rk-pc-ink));
}
.rk-pc-locked-size__hint {
  font: 400 13px/1.3 var(--rk-pc-font-body);
  color: var(--rk-pc-ink-muted);
  margin-left: auto;
}

.rk-pc-font-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}
@media (min-width: 560px) {
  .rk-pc-font-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
.rk-pc-font-tile {
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: pointer;
  text-align: center;
  font: inherit;
  color: inherit;
  transition: all 150ms ease;
  /* Grid children default to min-width:auto, which means their content
   * (the preview text) sets the floor — wide fonts like Big Shoulders
   * + Cardo at 22px were blowing the tile out past its column. min-width:0
   * lets the tile actually shrink to its track. min-width on the inner
   * elements + overflow handle truncation. */
  min-width: 0;
  overflow: hidden;
}
.rk-pc-font-tile:hover {
  border-color: var(--rk-pc-pink);
}
.rk-pc-font-tile.is-selected {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
  box-shadow: inset 0 0 0 2px var(--rk-pc-pink);
}
.rk-pc-font-tile__preview {
  /* Was 22px which pushed wide-glyph fonts past the tile edge. 18px sits
   * comfortably inside even at the 2-col mobile layout. */
  font-size: 18px;
  line-height: 1.15;
  color: var(--rk-pc-ink);
  min-height: 28px;
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  /* Block + min-width:0 inside a flex column so the ellipsis kicks in
   * when the rendered text exceeds the tile's content box. */
  min-width: 0;
  max-width: 100%;
}
.rk-pc-font-tile__name {
  font: 600 11px/1 var(--rk-pc-font-body);
  color: var(--rk-pc-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  /* Keep the customer-facing label on one line; long names like "Folio
   * Condensed" were wrapping awkwardly on narrow tiles. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 100%;
}

/* ─── Word Art style tiles (2026-07) ──────────────────────────────────── */
.rk-pc-wa-grid {
  grid-template-columns: 1fr;
}
@media (min-width: 560px) {
  .rk-pc-wa-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
.rk-pc-wa-tile {
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  padding: 10px 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: pointer;
  text-align: center;
  font: inherit;
  color: inherit;
  transition: all 150ms ease;
  min-width: 0;
  overflow: hidden;
}
.rk-pc-wa-tile:hover {
  border-color: var(--rk-pc-pink);
}
.rk-pc-wa-tile.is-selected {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
  box-shadow: inset 0 0 0 2px var(--rk-pc-pink);
}
.rk-pc-wa-tile__preview {
  display: block;
  background: #ececf0;
  border-radius: 8px;
  height: 76px;
  overflow: hidden;
}
.rk-pc-wa-tile__preview svg {
  display: block;
  width: 100%;
  height: 100%;
}
.rk-pc-wa-tile__name {
  font: 600 12px/1.1 var(--rk-pc-font-body);
  color: var(--rk-pc-ink);
}
.rk-pc-wa-tile__fidelity {
  font: 400 10px/1.2 var(--rk-pc-font-body);
  color: var(--rk-pc-muted);
}

/* ─── Occasion urgency line (2026-07-19) ──────────────────────────────── */
.rk-pc-occasion {
  display: flex;
  flex-direction: column;
  gap: 3px;
  background: var(--rk-pc-pink-pale);
  border: 1.5px dashed var(--rk-pc-pink);
  border-radius: var(--rk-pc-r-md);
  padding: 10px 14px;
  margin: 10px 0;
  font: 400 13px/1.45 var(--rk-pc-font-body);
  color: var(--rk-pc-ink);
}
.rk-pc-occasion__line {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.rk-pc-occasion__chip {
  font: 600 11px/1 var(--rk-pc-font-body);
  color: #fff;
  background: var(--rk-pc-pink);
  border-radius: 999px;
  padding: 4px 9px;
  white-space: nowrap;
}
.rk-pc-occasion__note {
  font-size: 12px;
  color: var(--rk-pc-plum-soft);
}

/* ─── Starter examples (2026-07-19) ───────────────────────────────────── */
.rk-pc-starters {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}
.rk-pc-starters__title {
  font: 600 14px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-starters__sub {
  font-size: 12px;
  color: var(--rk-pc-muted);
  margin-bottom: 4px;
}
.rk-pc-starters__row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}
@media (min-width: 560px) {
  .rk-pc-starters__row {
    grid-template-columns: repeat(4, 1fr);
  }
}
.rk-pc-starters__tile {
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  padding: 14px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 64px;
  overflow: hidden;
  transition: all 150ms ease;
}
.rk-pc-starters__tile:hover {
  border-color: var(--rk-pc-pink);
}
.rk-pc-starters__preview {
  font-size: 17px;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.rk-pc-starters__tile svg {
  display: block;
  max-height: 56px;
}

/* ─── Stitched-examples strip (2026-07-19) ───────────────────────────── */
.rk-pc-examples {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: 14px 0;
}
.rk-pc-examples__title {
  font: 600 15px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-examples__sub {
  font-size: 12px;
  color: var(--rk-pc-muted);
  margin-bottom: 6px;
}
.rk-pc-examples__row {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  padding-bottom: 6px;
  -webkit-overflow-scrolling: touch;
}
.rk-pc-examples__card {
  flex: 0 0 auto;
  width: 148px;
  margin: 0;
  scroll-snap-align: start;
}
.rk-pc-examples__card img {
  width: 148px;
  height: 148px;
  object-fit: cover;
  border-radius: var(--rk-pc-r-md);
  display: block;
  background: var(--rk-pc-card);
}
.rk-pc-examples__card figcaption {
  font-size: 11px;
  line-height: 1.35;
  color: var(--rk-pc-muted);
  padding-top: 4px;
}

/* ─── Size slider ─────────────────────────────────────────────────────── */

.rk-pc-slider {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.rk-pc-slider__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.rk-pc-slider__value {
  font: 800 22px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
}
.rk-pc-slider__value small {
  font: 600 12px/1 var(--rk-pc-font-body);
  color: var(--rk-pc-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.rk-pc-slider__tier-label {
  font: 700 14px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-forest);
  text-align: right;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.rk-pc-slider__tier-label small {
  font: 600 10px/1 var(--rk-pc-font-body);
  color: var(--rk-pc-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.rk-pc-slider__tier-label.is-fee {
  color: var(--rk-pc-plum);
}

.rk-pc-slider__wrap {
  position: relative;
  height: 28px;
  display: flex;
  align-items: center;
}
.rk-pc-slider__track {
  position: relative;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: var(--rk-pc-hairline-soft);
  overflow: hidden;
}
.rk-pc-slider__band {
  position: absolute;
  top: 0;
  bottom: 0;
}
.rk-pc-slider__band.is-free {
  background: var(--rk-pc-mint-soft);
}
.rk-pc-slider__band.is-paid-1 {
  background: var(--rk-pc-pink-soft);
}
.rk-pc-slider__band.is-paid-2 {
  background: var(--rk-pc-amber);
}
.rk-pc-slider__band.is-paid-3 {
  background: var(--rk-pc-plum-pale);
}
.rk-pc-slider__split {
  position: absolute;
  top: 4px;
  bottom: 4px;
  width: 2px;
  background: rgba(17, 17, 17, 0.18);
  border-radius: 2px;
  transform: translateX(-1px);
  z-index: 2;
}
.rk-pc-slider__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  appearance: none;
  background: transparent;
  z-index: 3;
}
.rk-pc-slider__input::-webkit-slider-thumb {
  appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: var(--rk-pc-pink);
  border: 3px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  cursor: grab;
}
.rk-pc-slider__input::-moz-range-thumb {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: var(--rk-pc-pink);
  border: 3px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  cursor: grab;
}

/* Tier cells row under the slider: one per SizeTier, each showing its
 * max-mm range + price. Current tier gets a subtle pink halo. */
.rk-pc-slider__tiers {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 6px;
  margin-top: 4px;
}
.rk-pc-slider__tier-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 4px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 11px;
  line-height: 1.2;
  background: var(--rk-pc-hairline-soft);
  cursor: pointer;
  font-family: inherit;
  -webkit-appearance: none;
  appearance: none;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.rk-pc-slider__tier-cell:hover {
  transform: translateY(-1px);
  box-shadow: 0 0 0 2px var(--rk-pc-pink-pale);
}
.rk-pc-slider__tier-cell small {
  font-size: 10px;
  color: var(--rk-pc-ink-2);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.rk-pc-slider__tier-cell strong {
  font: 700 12px/1 var(--rk-pc-font-display);
}
.rk-pc-slider__tier-cell.is-free {
  background: var(--rk-pc-mint-soft);
}
.rk-pc-slider__tier-cell.is-free strong {
  color: var(--rk-pc-forest);
}
.rk-pc-slider__tier-cell.is-paid-1 {
  background: var(--rk-pc-pink-soft);
}
.rk-pc-slider__tier-cell.is-paid-1 strong {
  color: var(--rk-pc-plum);
}
.rk-pc-slider__tier-cell.is-paid-2 {
  background: var(--rk-pc-amber);
}
.rk-pc-slider__tier-cell.is-paid-2 strong {
  color: var(--rk-pc-amber-ink);
}
.rk-pc-slider__tier-cell.is-paid-3 {
  background: var(--rk-pc-plum-pale);
}
.rk-pc-slider__tier-cell.is-paid-3 strong {
  color: var(--rk-pc-plum);
}
.rk-pc-slider__tier-cell.is-active {
  border-color: var(--rk-pc-pink);
  box-shadow: 0 0 0 2px var(--rk-pc-pink-pale);
  transform: translateY(-1px);
}

/* Legend row for non-tiered placements. */
.rk-pc-slider__legend {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: var(--rk-pc-muted);
}
.rk-pc-slider__legend strong {
  font: 700 12px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-slider__legend-note {
  font-style: italic;
}

/* ─── Price breakdown disclosure ──────────────────────────────────────── */

.rk-pc-breakdown {
  margin: 0;
  border-top: 1px dashed var(--rk-pc-hairline);
  padding-top: 10px;
}
.rk-pc-breakdown__summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  list-style: none;
  cursor: pointer;
  padding: 4px 2px;
  font-size: 12px;
  color: var(--rk-pc-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  user-select: none;
}
.rk-pc-breakdown__summary::-webkit-details-marker {
  display: none;
}
.rk-pc-breakdown__chev {
  font-size: 10px;
  transition: transform 150ms ease;
  color: var(--rk-pc-plum);
}
.rk-pc-breakdown[open] .rk-pc-breakdown__chev {
  transform: rotate(180deg);
}
.rk-pc-breakdown__list {
  list-style: none;
  margin: 8px 0 4px;
  padding: 0;
  max-height: 180px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.rk-pc-breakdown__row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 13px;
  color: var(--rk-pc-ink-2);
}
.rk-pc-breakdown__row-label {
  flex: 1;
  min-width: 0;
}
.rk-pc-breakdown__row-cents {
  font-weight: 600;
  color: var(--rk-pc-plum);
  white-space: nowrap;
}

/* ─── Add to cart bar: inline below the customiser content ───────────── */

.rk-pc-foot {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 20px 18px;
  border-top: 1px solid var(--rk-pc-hairline-soft);
}
.rk-pc-total-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.rk-pc-total-row__label {
  font-size: 13px;
  color: var(--rk-pc-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.rk-pc-total-row__value {
  font: 800 24px/1 var(--rk-pc-font-display);
}
/* Threshold unlock line under the item total: "You're $15 from free AU
 * shipping" / earned-it state. Muted by default, green once cleared. */
.rk-pc-unlock-row {
  margin-top: 4px;
  font-size: 13px;
  color: var(--rk-pc-muted);
  text-align: right;
}
.rk-pc-unlock-row--done {
  color: #1a7f37;
}
/* Post-ATC "complete the set" card + the View-cart link in the banner. */
.rk-pc-banner__cart-link {
  margin-left: 8px;
  padding: 4px 12px;
  border: 2px solid currentColor;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font: 700 12px/1 var(--rk-pc-font-display);
  cursor: pointer;
}
.rk-pc-second-item {
  margin: 10px 0 4px;
  padding: 14px;
  background: #fff;
  border: 2px solid var(--rk-pc-ink);
  border-radius: 14px;
}
.rk-pc-second-item__head {
  font: 800 12px/1 var(--rk-pc-font-display);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 10px;
}
.rk-pc-second-item__row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.rk-pc-second-item__thumb {
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
}
.rk-pc-second-item__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.rk-pc-second-item__meta {
  min-width: 0;
}
.rk-pc-second-item__title {
  font-weight: 700;
  font-size: 14px;
}
.rk-pc-second-item__sub {
  font-size: 12px;
  color: var(--rk-pc-muted);
  margin-top: 2px;
}
.rk-pc-second-item__price {
  margin-top: 4px;
  font: 800 15px/1 var(--rk-pc-font-display);
}
.rk-pc-second-item__disc {
  margin-left: 8px;
  font: 700 11px/1 var(--rk-pc-font-body, inherit);
  color: var(--rk-pc-pink-deep, #b3186d);
  background: #fce7f1;
  border-radius: 6px;
  padding: 3px 7px;
  vertical-align: middle;
}
.rk-pc-second-item__value {
  margin-top: 10px;
  font-size: 12.5px;
  font-weight: 700;
  color: #1a7f37;
}
.rk-pc-second-item__picker {
  margin-top: 10px;
}
.rk-pc-second-item__cta {
  margin-top: 12px;
  width: 100%;
  background: var(--rk-pc-pink);
  color: var(--rk-pc-ink);
  border: 2px solid var(--rk-pc-ink);
  border-radius: 999px;
  padding: 12px 16px;
  font: 700 14px/1 var(--rk-pc-font-display);
  cursor: pointer;
}
.rk-pc-second-item__cta:disabled {
  opacity: 0.55;
  cursor: default;
}

/* Remove-background toggle pill on the live preview. */
.rk-pc-pv-bgtoggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 8px 0 2px;
  padding: 6px 12px;
  background: #fff;
  border: 1.5px solid rgba(0, 0, 0, 0.16);
  border-radius: 999px;
  font-size: 12.5px;
  cursor: pointer;
  user-select: none;
}
.rk-pc-pv-bgtoggle input {
  accent-color: var(--rk-pc-pink);
  margin: 0;
}
.rk-pc-pv-bgtoggle__busy {
  color: var(--rk-pc-muted);
  font-style: italic;
}

/* Mobile sticky total bar: phones only. Fixed to the viewport bottom with
 * safe-area padding; desktop never sees it. */
.rk-pc-mobile-bar {
  display: none;
}
@media (max-width: 640px) {
  .rk-pc-mobile-bar {
    /* Portalled to body: font/colour no longer inherit from .rk-pc-root. */
    font-family: var(--rk-pc-font-body);
    color: var(--rk-pc-ink);
    display: flex;
    /* Wave 1: the trust caption rides as a full-width first row. */
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 60;
    background: #fff;
    border-top: 2px solid var(--rk-pc-ink);
    padding: 8px 16px calc(10px + env(safe-area-inset-bottom, 0px));
    transition: transform 0.28s ease, opacity 0.28s ease;
  }
  /* 2026-07-10: slide the bar away while the customer is actively in the
     designer (or typing) so it never covers the thread picker / Save spot;
     it returns when they scroll off the editor. */
  .rk-pc-mobile-bar.is-suppressed {
    transform: translateY(110%);
    opacity: 0;
    pointer-events: none;
  }
  /* Wave 1 trust strip inside the sticky bar - slim, one line where it
     fits, muted so it never competes with the price + button row. */
  .rk-pc-mobile-bar__trust {
    flex: 1 1 100%;
    margin-bottom: -4px;
    font-size: 10.5px;
    line-height: 1.3;
    color: var(--rk-pc-muted);
    text-align: center;
  }
  .rk-pc-mobile-bar__label {
    display: block;
    font-size: 10.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--rk-pc-muted);
  }
  .rk-pc-mobile-bar__value {
    font: 800 18px/1.1 var(--rk-pc-font-display);
  }
  .rk-pc-mobile-bar__btn {
    background: var(--rk-pc-pink);
    color: var(--rk-pc-ink);
    border: 2px solid var(--rk-pc-ink);
    border-radius: 999px;
    padding: 10px 16px;
    font: 700 13px/1 var(--rk-pc-font-display);
    white-space: nowrap;
    cursor: pointer;
  }
}

/* Sold-out pre-order note + CTA accent. */
.rk-pc-preorder-note {
  margin: 10px 0 8px;
  padding: 10px 12px;
  font-size: 12.5px;
  line-height: 1.45;
  color: #7a4a00;
  background: #fff3dc;
  border: 1px solid #e8b95a;
  border-radius: 10px;
}
.rk-pc-cta.is-preorder {
  background: #ffd27a;
}

/* Reassurance line directly above the add-to-cart button. */
.rk-pc-trust-line {
  margin: 10px 0 8px;
  font-size: 12px;
  color: var(--rk-pc-muted);
  text-align: center;
}
/* Add-to-cart failure: inline + retryable, right above the button, so a
   transient add error never wipes the whole customiser + finished design. */
.rk-pc-atc-error {
  margin: 0 0 10px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #fdecef;
  border: 1px solid #f3b6c2;
  color: #a01235;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
}
/* Wave 1: digitising trust strip at the price area - slightly tighter
   than the review line so the foot doesn't read as a wall of captions. */
.rk-pc-trust-line--digitising {
  margin: 8px 0 4px;
}
.rk-pc-trust-line span[aria-hidden] {
  color: #e8a33d;
  letter-spacing: 0.1em;
}
.rk-pc-cta {
  width: 100%;
  background: var(--rk-pc-pink);
  color: var(--rk-pc-ink);
  /* Match the theme's size-variant buttons: pink fill, dark outline. */
  border: 2px solid var(--rk-pc-ink);
  border-radius: 999px;
  padding: 16px 20px;
  font: 700 16px/1 var(--rk-pc-font-display);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 120ms ease;
}
.rk-pc-cta:hover:not(:disabled):not(.is-blocked) {
  background: var(--rk-pc-plum);
  color: #fff;
}
/* Wave 1: while add-to-cart is blocked the button LOOKS disabled but
   stays clickable as a guide (scroll to the blocking step + pulse the
   "Still to do" list). Mirrors the :disabled treatment; cursor stays
   pointer-ish via not-allowed so the state still reads as "not yet". */
.rk-pc-cta.is-blocked {
  opacity: 0.55;
  cursor: not-allowed;
}
.rk-pc-cta:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* On phones the add-to-cart bar stays inline at the end of the customiser
 * content (no fixed / sticky positioning) - just slightly smaller text. */
@media (max-width: 640px) {
  .rk-pc-total-row__value {
    font-size: 20px;
  }
  .rk-pc-cta {
    padding: 14px 18px;
    font-size: 15px;
  }
}

/* ─── States: loading / error / done ─────────────────────────────────── */

.rk-pc-banner {
  padding: 12px 16px;
  border-radius: var(--rk-pc-r-md);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.rk-pc-banner.is-error {
  background: #fde9ec;
  color: #9f1239;
  border: 1px solid #fbb6c2;
}
.rk-pc-banner.is-ok {
  background: var(--rk-pc-mint-soft);
  color: var(--rk-pc-forest);
  border: 1px solid var(--rk-pc-forest);
}

.rk-pc-loading {
  padding: 40px 20px;
  text-align: center;
  color: var(--rk-pc-muted);
  font-size: 14px;
}

/* ─── Saved-spot tiles ────────────────────────────────────────────────── */

.rk-pc-saved-spots {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.rk-pc-saved-spot {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  background: var(--rk-pc-pink-pale);
  border: 1px solid var(--rk-pc-pink);
  border-radius: var(--rk-pc-r-md);
}
.rk-pc-saved-spot__num {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: var(--rk-pc-pink);
  color: var(--rk-pc-plum);
  font: 700 12px/1 var(--rk-pc-font-display);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.rk-pc-saved-spot__body {
  min-width: 0;
}
.rk-pc-saved-spot__title {
  font: 700 14px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-saved-spot__sub {
  font: 12px/1.4 var(--rk-pc-font-body);
  color: var(--rk-pc-plum-soft);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rk-pc-saved-spot__price {
  font: 700 14px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-plum);
}
.rk-pc-saved-spot__actions {
  display: flex;
  gap: 6px;
}
.rk-pc-saved-spot__edit,
.rk-pc-saved-spot__remove {
  background: transparent;
  border: 1px solid var(--rk-pc-hairline);
  border-radius: 999px;
  padding: 4px 10px;
  font: 600 12px/1 var(--rk-pc-font-body);
  color: var(--rk-pc-ink);
  cursor: pointer;
  transition: all 120ms ease;
}
.rk-pc-saved-spot__edit:hover {
  border-color: var(--rk-pc-plum);
  color: var(--rk-pc-plum);
}
.rk-pc-saved-spot__remove {
  width: 26px;
  height: 26px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: var(--rk-pc-muted);
}
.rk-pc-saved-spot__remove:hover {
  border-color: #b91c1c;
  color: #b91c1c;
}

.rk-pc-add-spot-btn {
  background: transparent;
  border: 1.5px dashed var(--rk-pc-pink);
  color: var(--rk-pc-plum);
  border-radius: var(--rk-pc-r-md);
  padding: 12px 16px;
  font: 700 14px/1 var(--rk-pc-font-display);
  cursor: pointer;
  transition: all 120ms ease;
}
.rk-pc-add-spot-btn:hover {
  background: var(--rk-pc-pink-pale);
  border-style: solid;
}

/* ─── Logo reuse tile ─────────────────────────────────────────────────── */

.rk-pc-reuse-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.rk-pc-reuse-tile {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: var(--rk-pc-mint-soft);
  border: 1.5px solid var(--rk-pc-forest);
  border-radius: var(--rk-pc-r-md);
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
  transition: transform 120ms ease;
}
.rk-pc-reuse-tile:hover {
  transform: translateY(-1px);
}
.rk-pc-reuse-tile__check {
  font: 800 18px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-forest);
}
.rk-pc-reuse-tile__body {
  display: flex;
  flex-direction: column;
}
.rk-pc-reuse-tile__title {
  font: 700 13px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-reuse-tile__sub {
  font-size: 11px;
  color: var(--rk-pc-forest);
}

/* ─── Reuse design grid + cards ──────────────────────────────────────── */

.rk-pc-design-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-top: 6px;
}
@media (min-width: 560px) {
  .rk-pc-design-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
.rk-pc-design-card {
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: left;
  transition: all 150ms ease;
}
.rk-pc-design-card:hover {
  border-color: var(--rk-pc-pink);
  transform: translateY(-1px);
}
.rk-pc-design-card__canvas {
  min-height: 70px;
  background: var(--rk-pc-pink-pale);
  border-radius: var(--rk-pc-r-sm);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
}
.rk-pc-design-card__text {
  font-size: 20px;
  line-height: 1.1;
  color: var(--rk-pc-ink);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.rk-pc-design-card__logo {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--rk-pc-plum);
}
.rk-pc-design-card__logo-glyph {
  font-size: 18px;
  color: var(--rk-pc-pink);
}
.rk-pc-design-card__logo small {
  font: 11px/1.2 var(--rk-pc-font-body);
  color: var(--rk-pc-plum-soft);
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px;
}
.rk-pc-design-card__meta {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 6px;
  font: 12px/1.2 var(--rk-pc-font-body);
}
.rk-pc-design-card__placement {
  display: flex;
  flex-direction: column;
  font-weight: 600;
  color: var(--rk-pc-ink);
}
.rk-pc-design-card__placement small {
  font-weight: 400;
  color: var(--rk-pc-muted);
}
.rk-pc-design-card__from {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: var(--rk-pc-muted);
  font-size: 10px;
  text-align: right;
}
.rk-pc-design-card__from small {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ─── Arrangement picker (Both mode) ─────────────────────────────────── */

.rk-pc-arr-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
@media (min-width: 560px) {
  .rk-pc-arr-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
.rk-pc-arr-tile {
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: all 150ms ease;
}
.rk-pc-arr-tile:hover {
  border-color: var(--rk-pc-pink);
}
.rk-pc-arr-tile.is-selected {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
  box-shadow: inset 0 0 0 2px var(--rk-pc-pink);
}
.rk-pc-arr-tile__diagram {
  width: 56px;
  height: 38px;
  display: block;
}
.rk-pc-arr-tile__label {
  font: 600 11px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
  text-align: center;
}

/* ─── Editor sub-step accordion ───────────────────────────────────────── */

.rk-pc-substep {
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  background: #fff;
  margin-bottom: 10px;
  overflow: hidden;
  transition: border-color 150ms ease;
}
.rk-pc-substep.is-open {
  border-color: var(--rk-pc-pink);
}
.rk-pc-substep__header {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 13px 14px;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
}
.rk-pc-substep__header:hover {
  background: var(--rk-pc-hairline-soft);
}
.rk-pc-substep.is-open .rk-pc-substep__header:hover {
  background: transparent;
}
.rk-pc-substep__marker {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid var(--rk-pc-hairline);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
}
.rk-pc-substep.is-complete .rk-pc-substep__marker {
  background: var(--rk-pc-pink);
  border-color: var(--rk-pc-pink);
}
.rk-pc-substep__heading {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.rk-pc-substep__label {
  font: 700 13px/1.25 var(--rk-pc-font-display);
  color: var(--rk-pc-plum-soft);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.rk-pc-substep__summary {
  font: 500 12px/1.3 var(--rk-pc-font-body);
  color: var(--rk-pc-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rk-pc-substep__chevron {
  flex: 0 0 auto;
  color: var(--rk-pc-muted);
  font-size: 11px;
}
/* Done + collapsed steps swap the chevron for a "Change" action so the
   auto-advanced accordion stays visibly editable. */
.rk-pc-substep__chevron--change {
  font: 700 10px/1 var(--rk-pc-font-display);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--rk-pc-pink);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.rk-pc-substep__body {
  padding: 2px 14px 16px;
}

/* ─── Editor pending-state list ───────────────────────────────────────── */

.rk-pc-editor-missing {
  margin: 2px 0 12px;
  padding: 11px 14px;
  border-radius: var(--rk-pc-r-md);
  background: var(--rk-pc-pink-pale);
  border: 1px solid var(--rk-pc-hairline);
}
.rk-pc-editor-missing__head {
  display: block;
  font: 700 11px/1 var(--rk-pc-font-display);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--rk-pc-plum-soft);
  margin-bottom: 6px;
}
.rk-pc-editor-missing__list {
  margin: 0;
  padding-left: 18px;
  font: 500 12.5px/1.6 var(--rk-pc-font-body);
  color: var(--rk-pc-ink-2);
}
/* Wave 1: attention pulse when the customer taps the blocked add-to-cart
   button. The class is toggled imperatively with a timeout in App.tsx
   (guideBlockedAtc), so no state plumbing is needed. */
.rk-pc-editor-missing.is-pulsing,
/* RK26682: blocked add-to-cart with a finished but unsaved edit pulses
   the Save / Update button. */
.rk-pc-editor-actions .rk-pc-cta.is-pulsing,
/* A stage-owned field (e.g. the cap colour) is the blocker when the
   editor is closed, so it needs the same pulse. Its own box has no
   radius, hence the local one, otherwise the glow renders as a hard
   rectangle against the paper. */
.rk-pc-field.is-pulsing {
  animation: rk-pc-missing-pulse 0.8s ease-in-out 2;
}
.rk-pc-field.is-pulsing {
  border-radius: 10px;
}
@keyframes rk-pc-missing-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(243, 139, 185, 0);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(243, 139, 185, 0.55);
    border-color: var(--rk-pc-pink);
  }
}

/* ─── Upload-later ("I'll send it later") ─────────────────────────────── */

.rk-pc-upload-later {
  -webkit-appearance: none;
  appearance: none;
  display: block;
  width: 100%;
  margin: 8px 0 0;
  padding: 10px 14px;
  border: 1px dashed var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  background: none;
  font: 500 12.5px/1.5 var(--rk-pc-font-body);
  color: var(--rk-pc-ink-2);
  text-align: center;
  cursor: pointer;
}
.rk-pc-upload-later strong {
  color: var(--rk-pc-pink);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.rk-pc-upload-later-chip {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 8px 0 0;
  padding: 11px 14px;
  border-radius: var(--rk-pc-r-md);
  background: var(--rk-pc-pink-pale);
  border: 1px solid var(--rk-pc-hairline);
}
.rk-pc-upload-later-chip__tick {
  flex: 0 0 auto;
  font-size: 14px;
  color: var(--rk-pc-plum-soft);
}
.rk-pc-upload-later-chip__copy {
  flex: 1 1 auto;
  font: 500 12.5px/1.55 var(--rk-pc-font-body);
  color: var(--rk-pc-ink-2);
}
.rk-pc-upload-later-chip__undo {
  -webkit-appearance: none;
  appearance: none;
  flex: 0 0 auto;
  border: 0;
  background: none;
  padding: 0;
  font: 700 10px/1.4 var(--rk-pc-font-display);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--rk-pc-pink);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

/* ─── Section flow footer (Continue / Skip) ───────────────────────────── */

.rk-pc-section-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}
.rk-pc-section-continue {
  -webkit-appearance: none;
  appearance: none;
  border: 0;
  border-radius: var(--rk-pc-r-pill);
  background: var(--rk-pc-pink);
  color: var(--rk-pc-plum);
  font: 700 14px/1 var(--rk-pc-font-display);
  padding: 13px 28px;
  cursor: pointer;
  transition: filter 150ms ease;
}
.rk-pc-section-continue:hover {
  filter: brightness(0.96);
}
.rk-pc-section-skip {
  -webkit-appearance: none;
  appearance: none;
  border: 0;
  background: none;
  color: var(--rk-pc-muted);
  font: 600 13px/1 var(--rk-pc-font-body);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  padding: 11px 6px;
}
.rk-pc-section-skip:hover {
  color: var(--rk-pc-ink);
}

/* ─── Smooth collapse / expand (accordion bodies + sub-steps) ─────────── */
/* The grid-template-rows 0fr<->1fr trick animates real content height with
 * no JS measuring. The wrappers sit inside block parents (.rk-pc-sec /
 * .rk-pc-substep) so the flex-parent sizing quirk does not apply. */

.rk-pc-collapse {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 400ms cubic-bezier(0.4, 0, 0.2, 1);
}
.rk-pc-collapse[data-open="true"] {
  grid-template-rows: 1fr;
}
.rk-pc-collapse__clip {
  overflow: hidden;
  min-height: 0;
}
.rk-pc-collapse__clip > * {
  opacity: 0;
  transition: opacity 240ms ease;
}
.rk-pc-collapse[data-open="true"] > .rk-pc-collapse__clip > * {
  opacity: 1;
}

/* ─── Sub-step Confirm button ─────────────────────────────────────────── */

.rk-pc-substep-confirm-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}
.rk-pc-substep-confirm {
  -webkit-appearance: none;
  appearance: none;
  /* Match the theme's size-variant buttons: pink fill, dark outline. */
  border: 2px solid var(--rk-pc-ink);
  border-radius: var(--rk-pc-r-pill);
  background: var(--rk-pc-pink);
  color: var(--rk-pc-ink);
  font: 700 13px/1 var(--rk-pc-font-display);
  padding: 11px 24px;
  cursor: pointer;
  transition: filter 150ms ease;
}
.rk-pc-substep-confirm:hover:not(:disabled) {
  filter: brightness(0.96);
}
.rk-pc-substep-confirm:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ─── Editor save/cancel row (per-spot) ───────────────────────────────── */

.rk-pc-editor-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 6px;
}
.rk-pc-editor-actions .rk-pc-cta {
  width: auto;
  padding: 12px 22px;
  font-size: 14px;
}

.rk-pc-cta--ghost {
  background: transparent;
  color: var(--rk-pc-ink);
  border: 1.5px solid var(--rk-pc-hairline);
}
.rk-pc-cta--ghost:hover:not(:disabled) {
  background: var(--rk-pc-hairline-soft);
  color: var(--rk-pc-ink);
  border-color: var(--rk-pc-muted);
}

/* ─── Variant-not-picked gate ────────────────────────────────────────── */

.rk-pc-gate {
  padding: 28px 22px;
  border: 1px dashed var(--rk-pc-pink);
  border-radius: var(--rk-pc-r-lg);
  background: var(--rk-pc-pink-pale);
  text-align: center;
}
.rk-pc-gate__title {
  font: 700 16px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-plum);
  margin-bottom: 4px;
}
.rk-pc-gate__sub {
  font-size: 13px;
  color: var(--rk-pc-plum-soft);
}

/* ─── Thread colour picker ──────────────────────────────────────────── */

.rk-pc-threads {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.rk-pc-threads__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
}
.rk-pc-threads__heading {
  font: 700 14px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-threads__counter {
  font-size: 12px;
  color: var(--rk-pc-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
}
.rk-pc-threads__hint {
  margin: 0;
  padding: 8px 10px;
  background: var(--rk-pc-pink-pale);
  border-left: 3px solid var(--rk-pc-pink);
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--rk-pc-plum-soft);
}
.rk-pc-threads__foot {
  margin: 0;
  font-size: 12px;
  color: var(--rk-pc-muted);
}

/* "Match the design" option — full-width choice above the swatch grid. */
.rk-pc-thread-match {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  background: var(--rk-pc-card);
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
}
.rk-pc-thread-match:hover {
  border-color: var(--rk-pc-plum-soft);
}
.rk-pc-thread-match.is-selected {
  border-color: var(--rk-pc-plum);
  background: var(--rk-pc-pink-pale);
}
.rk-pc-thread-match__check {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: var(--rk-pc-hairline-soft);
  color: var(--rk-pc-plum-soft);
  font-size: 12px;
  font-weight: 700;
}
.rk-pc-thread-match.is-selected .rk-pc-thread-match__check {
  background: var(--rk-pc-plum);
  color: #fff;
}
.rk-pc-thread-match__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.rk-pc-thread-match__title {
  font-weight: 700;
  font-size: 14px;
  color: var(--rk-pc-ink);
}
.rk-pc-thread-match__sub {
  font-size: 12px;
  line-height: 1.4;
  color: var(--rk-pc-muted);
}
.rk-pc-thread-or {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--rk-pc-muted);
}
.rk-pc-thread-or::before,
.rk-pc-thread-or::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--rk-pc-hairline);
}
.rk-pc-threads__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 28px;
  align-items: center;
}
.rk-pc-threads__empty {
  font-size: 12px;
  color: var(--rk-pc-muted);
  font-style: italic;
}
.rk-pc-thread-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 4px 4px 6px;
  background: var(--rk-pc-card);
  border: 1px solid var(--rk-pc-hairline);
  border-radius: 999px;
  font-size: 12px;
}
.rk-pc-thread-chip__swatch {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}
.rk-pc-thread-chip__name {
  font-weight: 600;
  color: var(--rk-pc-ink);
}
.rk-pc-thread-chip__code {
  font-size: 10px;
  color: var(--rk-pc-muted);
  letter-spacing: 0.04em;
}
.rk-pc-thread-chip__x {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 0;
  background: var(--rk-pc-hairline-soft);
  color: var(--rk-pc-ink-2);
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.rk-pc-thread-chip__x:hover {
  background: var(--rk-pc-pink);
  color: var(--rk-pc-plum);
}
/* Phase B3: compact picker — just colour dots. Name + code live in the
 * picked-chips row above + the native title tooltip / aria-label. */
.rk-pc-thread-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
}
.rk-pc-thread-dot {
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  border-radius: 999px;
  border: 1.5px solid var(--rk-pc-hairline);
  padding: 0;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  transition: transform 120ms ease, box-shadow 120ms ease;
}
.rk-pc-thread-dot:hover:not(:disabled) {
  transform: scale(1.12);
}
.rk-pc-thread-dot.is-selected {
  box-shadow:
    0 0 0 2px var(--rk-pc-card),
    0 0 0 4px var(--rk-pc-pink);
}
.rk-pc-thread-dot:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* ─── Editor mount/unmount transition ────────────────────────────────── */

/* Enter animation only. The close is driven by a Web Animations API
 * animation kicked off from App.tsx, which interpolates height + opacity
 * from the measured natural height down to 0.
 *
 * CRITICAL: `min-height: 0` is required so the wrap can actually shrink
 * below its content's intrinsic size. The wrap is a flex item in the
 * `.rk-pc-sec__body` column, and flex items default to `min-height: auto`,
 * which clamps the rendered height to the content's natural size, making
 * the animation visually do nothing even though the height keyframe is
 * interpolating correctly. Same with `min-width`.
 *
 * On mount: opacity fades from 0→1 with a small translate-down. */
.rk-pc-editor-wrap {
  min-height: 0;
  min-width: 0;
  animation: rk-pc-editor-enter 240ms ease-out;
}
@keyframes rk-pc-editor-enter {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* Note: we intentionally do NOT silence this animation under
 * prefers-reduced-motion. The customer requested the eased motion as a
 * UX affordance and that signal trumps the generic OS preference here.
 * If it ever becomes a real accessibility complaint, prefer a SHORTER
 * fade (e.g. 200ms opacity-only) rather than dropping the animation. */

/* ─── Customer notes section ────────────────────────────────────────── */

.rk-pc-notes-label {
  display: block;
  font: 700 13px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
  margin-bottom: 8px;
}
.rk-pc-notes-input {
  width: 100%;
  padding: 12px 14px;
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  font: 14px/1.45 var(--rk-pc-font-body);
  color: var(--rk-pc-ink);
  resize: vertical;
  min-height: 96px;
  box-sizing: border-box;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.rk-pc-notes-input:focus {
  outline: 0;
  border-color: var(--rk-pc-pink);
  box-shadow: 0 0 0 3px var(--rk-pc-pink-pale);
}
.rk-pc-notes-input::placeholder {
  color: var(--rk-pc-muted);
}
.rk-pc-notes-foot {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  margin-top: 6px;
}
.rk-pc-notes-hint {
  font-size: 12px;
  color: var(--rk-pc-muted);
  font-style: italic;
}
.rk-pc-notes-count {
  font: 600 11px/1 var(--rk-pc-font-body);
  color: var(--rk-pc-muted);
  letter-spacing: 0.04em;
}
.rk-pc-notes-count.is-warning {
  color: var(--rk-pc-plum);
}

/* Thread colour dots inside the saved-spot tile. */
.rk-pc-saved-spot__threads {
  display: flex;
  gap: 4px;
  margin-top: 4px;
}
.rk-pc-saved-spot__thread {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

/* Phase 28: past-order lookup form inside ReuseSection.
 * Two inputs + a button on one line on desktop, stacked on small screens.
 * Error + result grid slot in underneath. */
.rk-pc-past-order {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.rk-pc-past-order__form {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
}
.rk-pc-past-order__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 200px;
  min-width: 0;
}
.rk-pc-past-order__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--rk-pc-ink-2);
  letter-spacing: 0.02em;
}
.rk-pc-past-order__input {
  border: 1px solid var(--rk-pc-hairline);
  background: var(--rk-pc-card);
  border-radius: var(--rk-pc-r-md);
  padding: 10px 12px;
  font: inherit;
  color: var(--rk-pc-ink);
  outline: none;
  transition: border-color 120ms ease;
  width: 100%;
}
.rk-pc-past-order__input:focus {
  border-color: var(--rk-pc-pink);
  box-shadow: 0 0 0 3px rgba(243, 139, 185, 0.18);
}
.rk-pc-past-order__btn {
  background: var(--rk-pc-plum);
  color: #fff;
  border: 0;
  border-radius: var(--rk-pc-r-md);
  padding: 11px 16px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background 120ms ease;
  align-self: stretch;
  /* Sit on its own line on narrow screens, otherwise grow to match
   * the input height. */
  flex: 0 0 auto;
}
.rk-pc-past-order__btn:hover:not(:disabled) {
  background: var(--rk-pc-plum-soft);
}
.rk-pc-past-order__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.rk-pc-past-order__error {
  background: #fff4f4;
  border: 1px solid #f1c4c4;
  color: #842727;
  padding: 10px 12px;
  border-radius: var(--rk-pc-r-md);
  font-size: 14px;
}

/* Phase 37: order-level add-on tiles. Care kit / Gift box / Rush /
 * etc. Each tile is a togglable button with a check pip + price. */
.rk-pc-order-addons {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
@media (min-width: 560px) {
  .rk-pc-order-addons {
    grid-template-columns: repeat(2, 1fr);
  }
}
.rk-pc-order-addons__tile {
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  padding: 12px 14px;
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: all 150ms ease;
  width: 100%;
}
.rk-pc-order-addons__tile:hover {
  border-color: var(--rk-pc-pink);
}
.rk-pc-order-addons__tile.is-selected {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
  box-shadow: inset 0 0 0 2px var(--rk-pc-pink);
}
.rk-pc-order-addons__check {
  width: 22px;
  height: 22px;
  border-radius: var(--rk-pc-r-pill);
  border: 1.5px solid var(--rk-pc-hairline);
  background: var(--rk-pc-card);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font: 700 14px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-plum);
  flex: 0 0 22px;
  transition: all 150ms ease;
}
.rk-pc-order-addons__tile.is-selected .rk-pc-order-addons__check {
  background: var(--rk-pc-plum);
  border-color: var(--rk-pc-plum);
  color: #fff;
}
.rk-pc-order-addons__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.rk-pc-order-addons__title {
  font: 700 14px/1.2 var(--rk-pc-font-display);
}
.rk-pc-order-addons__hint {
  font-size: 12px;
  color: var(--rk-pc-muted);
  line-height: 1.3;
}
.rk-pc-order-addons__price {
  font-weight: 700;
  color: var(--rk-pc-plum);
  flex: 0 0 auto;
}
/* Rush is added per garment, not per order. When picked, warn that it
   only speeds up this item so multi-item orders aren't misread. */
.rk-pc-order-addons__rush-note {
  margin: 10px 0 0;
  padding: 10px 12px;
  border-radius: var(--rk-pc-r-card, 10px);
  background: #fff4ec;
  border: 1.5px solid #f4731f;
  color: #8a3d0b;
  font-size: 12.5px;
  line-height: 1.45;
}
.rk-pc-order-addons__rush-note strong {
  font-weight: 700;
  color: #6f2f08;
}
/* Abandoned-design recovery: guest "save your design" capture card. Sits
   just above the add-to-cart button; shown only to guests with a real
   design in progress (logged-in shoppers already have an email on file). */
.rk-pc-savedesign {
  margin: 14px 0 4px;
  padding: 14px 16px;
  border-radius: var(--rk-pc-r-card, 12px);
  background: var(--rk-pc-pink-pale, #fdf2f8);
  border: 1.5px solid #f3b6d4;
}
.rk-pc-savedesign__head {
  font-weight: 700;
  font-size: 14px;
  color: var(--rk-pc-plum, #5c1756);
  margin-bottom: 2px;
}
.rk-pc-savedesign__copy {
  margin: 0 0 10px;
  font-size: 12.5px;
  line-height: 1.45;
  color: #4b3a47;
}
.rk-pc-savedesign__row {
  display: flex;
  gap: 8px;
  align-items: stretch;
}
.rk-pc-savedesign__input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 9px 12px;
  font-size: 13px;
  border-radius: 999px;
  border: 1.5px solid #f3b6d4;
  background: #fff;
  color: #2a2230;
}
.rk-pc-savedesign__input:focus {
  outline: none;
  border-color: var(--rk-pc-plum, #5c1756);
}
.rk-pc-savedesign__btn {
  flex: 0 0 auto;
  padding: 9px 14px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  border-radius: 999px;
  border: 0;
  background: var(--rk-pc-plum, #5c1756);
  color: #fff;
  cursor: pointer;
}
.rk-pc-savedesign__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.rk-pc-savedesign__consent {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-top: 9px;
  font-size: 11.5px;
  line-height: 1.4;
  color: #6b5566;
  cursor: pointer;
}
.rk-pc-savedesign__consent input {
  margin-top: 2px;
  flex: 0 0 auto;
}
.rk-pc-savedesign--done {
  background: #eef7e9;
  border-color: #b4daa6;
  font-size: 12.5px;
  line-height: 1.5;
  color: #2f5a3a;
}
.rk-pc-savedesign--done strong {
  color: #2f5a3a;
}

/* Phase 35e: variant-switch banner. Shown on remount when the customer
 * picks a new size/colour after configuring spots. */
.rk-pc-variant-switch-banner {
  background: var(--rk-pc-pink-pale);
  border: 1px solid var(--rk-pc-pink-soft);
  color: var(--rk-pc-ink);
  padding: 10px 14px;
  border-radius: var(--rk-pc-r-md);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.rk-pc-variant-switch-banner__text {
  flex: 1 1 240px;
  font-size: 14px;
}
.rk-pc-variant-switch-banner__btn {
  background: var(--rk-pc-plum);
  color: #fff;
  border: 0;
  border-radius: var(--rk-pc-r-pill);
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

/* Phase 35f: skeleton shown while the customiser's config is loading. */
.rk-pc-skeleton {
  background: var(--rk-pc-card);
  border: 1px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-lg);
  padding: 16px;
  margin-bottom: 12px;
}
.rk-pc-skeleton__head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.rk-pc-skeleton__num {
  width: 32px;
  height: 32px;
  border-radius: var(--rk-pc-r-pill);
  background: linear-gradient(90deg, #f3f4f6 0%, #ececec 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: rk-pc-shimmer 1.4s ease-in-out infinite;
}
.rk-pc-skeleton__title {
  height: 18px;
  flex: 1;
  border-radius: 4px;
  background: linear-gradient(90deg, #f3f4f6 0%, #ececec 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: rk-pc-shimmer 1.4s ease-in-out infinite;
}
.rk-pc-skeleton__body {
  margin-top: 12px;
}
.rk-pc-skeleton__tiles {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.rk-pc-skeleton__tile {
  height: 80px;
  border-radius: var(--rk-pc-r-md);
  background: linear-gradient(90deg, #f3f4f6 0%, #ececec 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: rk-pc-shimmer 1.4s ease-in-out infinite;
}
@keyframes rk-pc-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Phase 35g: workflow + care footer. Lives below the CTA. */
.rk-pc-workflow {
  margin-top: 20px;
  padding: 18px;
  background: var(--rk-pc-pink-pale);
  border: 1px solid var(--rk-pc-pink-soft);
  border-radius: var(--rk-pc-r-lg);
}
.rk-pc-workflow__title {
  margin: 0 0 12px;
  font: 700 16px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-plum);
}
.rk-pc-workflow__steps {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.rk-pc-workflow__steps li {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.4;
}
.rk-pc-workflow__step-num {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border-radius: var(--rk-pc-r-pill);
  background: var(--rk-pc-plum);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font: 700 12px/1 var(--rk-pc-font-display);
}
.rk-pc-workflow__care {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--rk-pc-pink-soft);
  font-size: 13px;
  color: var(--rk-pc-ink-2);
  line-height: 1.5;
}

/* ─── Pre-designer summary (intro panel) ──────────────────────────────── */

.rk-pc-intro {
  background: var(--rk-pc-card);
  border: 1px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-xl);
  box-shadow: var(--rk-pc-sh-card);
  padding: 20px;
  margin-bottom: 14px;
}
.rk-pc-intro__title {
  margin: 0 0 16px;
  font: 800 18px/1.2 var(--rk-pc-font-display);
  letter-spacing: -0.01em;
  color: var(--rk-pc-plum);
}
.rk-pc-intro__shipby {
  margin: 14px 0 0;
  padding: 10px 12px;
  font-size: 12.5px;
  line-height: 1.45;
  color: #1f5130;
  background: #eef7ef;
  border: 1px solid #bfe0c6;
  border-radius: 10px;
}
.rk-pc-intro__shipby strong { white-space: nowrap; }
.rk-pc-intro__steps {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}
.rk-pc-intro__steps li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.rk-pc-intro__num {
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: var(--rk-pc-r-pill);
  border: 2px solid var(--rk-pc-ink);
  background: var(--rk-pc-pink);
  color: var(--rk-pc-ink);
  font: 800 14px/1 var(--rk-pc-font-display);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
/* Brand reference palette: each step number gets its own colour, all
 * black-outlined for that bold sticker look. */
.rk-pc-intro__steps li:nth-child(1) .rk-pc-intro__num {
  background: #f38bb9;
}
.rk-pc-intro__steps li:nth-child(2) .rk-pc-intro__num {
  background: #b4daa6;
}
.rk-pc-intro__steps li:nth-child(3) .rk-pc-intro__num {
  background: #7cccf2;
}
.rk-pc-intro__steps li div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.rk-pc-intro__steps strong {
  font: 700 14px/1.3 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-intro__steps span {
  font-size: 13px;
  line-height: 1.4;
  color: var(--rk-pc-muted);
}
@media (max-width: 560px) {
  .rk-pc-intro__steps {
    grid-template-columns: 1fr;
  }
}

/* Phase 39c: multi-image upload UI on pet flow. */
.rk-pc-extra-uploads {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 6px 0;
}
.rk-pc-extra-uploads__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--rk-pc-pink-pale);
  border: 1px solid var(--rk-pc-pink-soft);
  border-radius: var(--rk-pc-r-md);
  padding: 8px 12px;
  font-size: 13px;
}
.rk-pc-extra-uploads__name {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: var(--rk-pc-ink);
}
.rk-pc-extra-uploads__remove {
  flex: 0 0 auto;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-size: 16px;
  color: var(--rk-pc-plum);
  padding: 0 6px;
}
.rk-pc-extra-uploads__remove:hover {
  color: var(--rk-pc-pink);
}
.rk-pc-extra-upload-add {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.rk-pc-extra-upload-add__btn {
  display: inline-block;
  background: var(--rk-pc-card);
  border: 1.5px dashed var(--rk-pc-pink);
  color: var(--rk-pc-plum);
  border-radius: var(--rk-pc-r-md);
  padding: 10px 14px;
  font: 700 13px/1 var(--rk-pc-font-display);
  cursor: pointer;
  text-align: center;
  transition: all 150ms ease;
}
.rk-pc-extra-upload-add__btn:hover {
  background: var(--rk-pc-pink-pale);
}
.rk-pc-extra-upload-add__error {
  font-size: 12px;
  color: #842727;
}

/* Phase 33b/c: pet-portrait config block (count stepper + style toggle)
 * rendered under the upload step. */
.rk-pc-addon-config {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px dashed var(--rk-pc-hairline);
}
.rk-pc-addon-config__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.rk-pc-addon-config__label {
  font: 600 13px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-addon-config__note {
  font-size: 12px;
  line-height: 1.45;
  color: var(--rk-pc-plum-soft);
  background: var(--rk-pc-pink-pale);
  border-radius: 8px;
  padding: 8px 10px;
}
.rk-pc-addon-config__stepper {
  display: inline-flex;
  gap: 8px;
}
.rk-pc-addon-config__step {
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  font: 700 16px/1 var(--rk-pc-font-display);
  width: 44px;
  height: 44px;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--rk-pc-ink);
}
.rk-pc-addon-config__step:hover {
  border-color: var(--rk-pc-pink);
}
.rk-pc-addon-config__step.is-selected {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
  box-shadow: inset 0 0 0 2px var(--rk-pc-pink);
}
.rk-pc-addon-config__hint {
  font-size: 12px;
  color: var(--rk-pc-muted);
}

/* Phase 33d: sleeve cross-sell section. */
.rk-pc-sleeve-cross-sell {
  background: linear-gradient(180deg, rgba(243, 139, 185, 0.1), rgba(252, 218, 244, 0.18));
  border: 1px solid var(--rk-pc-pink-soft);
}
/* Phase 42d: name input inside the sleeve cross-sell card. */
.rk-pc-sleeve-name {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}
.rk-pc-sleeve-name__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--rk-pc-ink-2);
  letter-spacing: 0.02em;
}
.rk-pc-sleeve-name__input {
  border: 1.5px solid var(--rk-pc-pink-soft);
  background: var(--rk-pc-card);
  border-radius: var(--rk-pc-r-md);
  padding: 10px 12px;
  font: inherit;
  font-size: 15px;
  color: var(--rk-pc-ink);
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  width: 100%;
}
.rk-pc-sleeve-name__input:focus {
  border-color: var(--rk-pc-pink);
  box-shadow: 0 0 0 3px rgba(243, 139, 185, 0.22);
}
.rk-pc-sleeve-name__hint {
  font-size: 11px;
  color: var(--rk-pc-muted);
}
.rk-pc-sleeve-cta:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.rk-pc-sleeve-emojis {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
}
.rk-pc-sleeve-emojis__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--rk-pc-ink-2);
  letter-spacing: 0.02em;
}
.rk-pc-sleeve-emojis__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.rk-pc-sleeve-emojis__chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-pill);
  padding: 8px 14px;
  cursor: pointer;
  font: inherit;
  color: var(--rk-pc-ink);
  transition: all 150ms ease;
}
.rk-pc-sleeve-emojis__chip:hover {
  border-color: var(--rk-pc-pink);
}
.rk-pc-sleeve-emojis__chip.is-selected {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
  box-shadow: inset 0 0 0 2px var(--rk-pc-pink);
}
.rk-pc-sleeve-emojis__glyph {
  font-size: 18px;
  line-height: 1;
}
.rk-pc-sleeve-emojis__name {
  font-size: 13px;
  font-weight: 600;
}
.rk-pc-sleeve-emojis__price {
  font-size: 12px;
  color: var(--rk-pc-muted);
  font-weight: 600;
}
.rk-pc-sleeve-cta {
  width: 100%;
  background: var(--rk-pc-plum);
  color: #fff;
  border: 0;
  border-radius: var(--rk-pc-r-md);
  padding: 12px 16px;
  font: 700 14px/1 var(--rk-pc-font-display);
  cursor: pointer;
  transition: background 120ms ease;
}
.rk-pc-sleeve-cta:hover {
  background: var(--rk-pc-plum-soft);
}

/* 2026-07-02 CRO 2.3: back-of-garment cross-sell card. Shares the
 * prompted-card shape (and the sleeve CTA button styles) with the
 * sleeve cross-sell; plum-tinted so the two offers read as distinct
 * when stacked. */
.rk-pc-back-cross-sell {
  background: linear-gradient(180deg, rgba(93, 62, 133, 0.06), rgba(93, 62, 133, 0.12));
  border: 1px solid var(--rk-pc-hairline);
}
.rk-pc-back-reuse {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  padding: 10px 12px;
  margin-bottom: 14px;
  min-width: 0;
}
.rk-pc-back-reuse__glyph {
  font-size: 18px;
  line-height: 1;
  color: var(--rk-pc-plum);
}
.rk-pc-back-reuse__label {
  font-size: 14px;
  font-weight: 600;
  color: var(--rk-pc-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.rk-pc-back-reuse__arrow {
  color: var(--rk-pc-muted);
  flex: 0 0 auto;
}
.rk-pc-back-reuse__target {
  font-size: 12px;
  color: var(--rk-pc-ink-2);
  flex: 1 1 auto;
}

/* ─── Wave 3: consolidated "Finish your order" screen ─────────────────── */
/* A titled group that gathers the approval choice, paid add-ons, back
 * cross-sell, complete-the-set card and notes into one screen. Each child
 * keeps the standard .rk-pc-sec card shape; this wrapper just adds the
 * heading and the spacing between them. */
.rk-pc-finish {
  margin-bottom: 14px;
}
.rk-pc-finish__head {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 4px 2px 12px;
}
.rk-pc-finish__title {
  font: 700 20px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-finish__sub {
  font-size: 13px;
  color: var(--rk-pc-muted);
}
/* Approval is a FREE decision, not a paid upsell - amber-tinted + a left
 * accent bar so it reads as distinct from the pink add-on tiles and can't
 * be missed (it gates production holds). */
.rk-pc-finish__approval.rk-pc-sec.is-active {
  border-color: var(--rk-pc-amber);
  box-shadow: 0 6px 18px rgba(241, 198, 114, 0.22);
  background: linear-gradient(
    180deg,
    rgba(241, 198, 114, 0.08),
    rgba(241, 198, 114, 0.03)
  );
}
.rk-pc-finish__approval .rk-pc-sec__head {
  border-left: 4px solid var(--rk-pc-amber);
}
.rk-pc-finish__approval .rk-pc-sec__num {
  background: var(--rk-pc-amber);
  color: var(--rk-pc-amber-ink);
}
.rk-pc-finish__free-pill {
  flex-shrink: 0;
  align-self: flex-start;
  font: 700 11px/1 var(--rk-pc-font-display);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--rk-pc-amber-ink);
  background: var(--rk-pc-amber);
  border-radius: var(--rk-pc-r-pill);
  padding: 5px 10px;
}
/* Notes disclosure sits quietly at the foot of the finish screen - a
 * neutral card so it never competes with the approval + add-on choices. */
.rk-pc-finish__notes .rk-pc-sec__head {
  padding-top: 14px;
  padding-bottom: 14px;
}

/* Phase 31: design-proof tile (clickable to ingest). Replaces the
 * earlier compact pills which wrapped badly. Each tile is a button
 * that takes a full row at narrow widths and floats inline at wider
 * widths; the CTA always lives on the same line as the filename. */

/* Phase 30: design-proof PDF row inside the past-order lookup pane.
 * Each PDF gets a downloadable pill; row wraps to multiple lines when
 * there are several proofs (multi-spot orders are common). */
.rk-pc-past-proofs {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
}
.rk-pc-past-proofs__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--rk-pc-ink-2);
  letter-spacing: 0.02em;
}
.rk-pc-past-proofs__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.rk-pc-past-proofs__tile {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--rk-pc-card);
  border: 1px solid var(--rk-pc-hairline);
  color: var(--rk-pc-ink);
  padding: 10px 12px;
  border-radius: var(--rk-pc-r-md);
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease, transform 120ms ease;
  width: 100%;
  max-width: 360px;
  flex: 1 1 280px;
}
.rk-pc-past-proofs__tile:hover:not(:disabled) {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
}
.rk-pc-past-proofs__tile:active:not(:disabled) {
  transform: scale(0.99);
}
.rk-pc-past-proofs__tile:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.rk-pc-past-proofs__icon {
  font-size: 10px;
  font-weight: 700;
  color: #b3261e;
  background: rgba(179, 38, 30, 0.12);
  padding: 3px 6px;
  border-radius: var(--rk-pc-r-sm);
  letter-spacing: 0.05em;
  flex: 0 0 auto;
}
.rk-pc-past-proofs__name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 auto;
  min-width: 0;
}
.rk-pc-past-proofs__cta {
  flex: 0 0 auto;
  background: var(--rk-pc-plum);
  color: #fff;
  padding: 4px 10px;
  border-radius: var(--rk-pc-r-pill);
  font-size: 12px;
  font-weight: 600;
}

/* ─── Live embroidery preview (visualiser) ────────────────────────────── */

/* Two layouts, one markup:
 *  - Narrow screens (default below): an inline collapsible panel at the
 *    top of the customiser. It takes real layout space — no fixed /
 *    sticky / z-index — so it can never cover the options or the page.
 *  - Desktop (min-width: 1025px, overrides further down): a sticky
 *    flyout on the customiser's left edge — a small icon tab pops the
 *    preview out beside the options without pushing anything down.
 * PreviewCanvas stays mounted in both so its caches survive. */
.rk-pc-pv-mount {
  margin-bottom: 14px;
}

/* "Visualise the design" toggle — full-width inline button. */
.rk-pc-pv-tab {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  background: var(--rk-pc-plum);
  color: #fff;
  border: 0;
  border-radius: var(--rk-pc-r-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 140ms ease;
}
.rk-pc-pv-tab:hover {
  background: var(--rk-pc-plum-soft);
}
.rk-pc-pv-tab.is-open {
  border-radius: var(--rk-pc-r-md) var(--rk-pc-r-md) 0 0;
}
.rk-pc-pv-tab svg {
  flex: 0 0 auto;
}
.rk-pc-pv-tab__label {
  flex: 1 1 auto;
  text-align: left;
}
.rk-pc-pv-tab__chevron {
  display: grid;
  place-items: center;
  transition: transform 240ms ease;
}
.rk-pc-pv-tab.is-open .rk-pc-pv-tab__chevron {
  transform: rotate(180deg);
}

/* Collapsible body — grid row 0fr -> 1fr keeps PreviewCanvas mounted so
 * its caches (bg-removal, drawing filter) survive a close/open, and the
 * stage still reports a real width while collapsed. */
.rk-pc-pv-dock {
  display: grid;
  grid-template-rows: 0fr;
}
/* Transition is enabled only after the drawer's first paint (.is-ready),
 * so it never animates itself open/closed on page load. */
.rk-pc-pv-mount.is-ready .rk-pc-pv-dock {
  transition: grid-template-rows 320ms cubic-bezier(0.4, 0, 0.2, 1);
}
.rk-pc-pv-dock.is-open {
  grid-template-rows: 1fr;
}
.rk-pc-pv-dock__clip {
  overflow: hidden;
  min-height: 0;
}
.rk-pc-pv-dock .rk-pc-pv-canvas {
  width: 100%;
  max-width: 520px;
  margin: 10px auto 4px;
}

/* The rail wrapper is a no-op on narrow screens (the inline panel needs
 * the tab + dock as plain in-flow blocks). It becomes the sticky element
 * on desktop. */
.rk-pc-pv-rail {
  display: contents;
}

/* Desktop: a sticky left-edge flyout. The mount is an absolutely
 * positioned layer that spans the FULL customiser height; the rail
 * sticks inside it. Because the rail's containing block is the mount,
 * the rail (and the dock it holds) can never scroll past the bottom of
 * the customiser — it stops cleanly instead of sliding under the
 * section below (e.g. the reviews). */
@media (min-width: 1025px) {
  .rk-pc-pv-mount {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 0;
    margin-bottom: 0;
    z-index: 5;
    pointer-events: none;
  }
  .rk-pc-pv-rail {
    display: block;
    position: sticky;
    top: var(--rk-pc-pv-top, 180px);
    width: 0;
  }
  .rk-pc-pv-tab {
    position: absolute;
    top: 0;
    left: 0;
    transform: translateX(-100%);
    z-index: 3;
    display: grid;
    place-items: center;
    width: 44px;
    height: 50px;
    padding: 0;
    border-radius: var(--rk-pc-r-md) 0 0 var(--rk-pc-r-md);
    box-shadow: -3px 3px 16px rgba(17, 17, 17, 0.26);
    pointer-events: auto;
  }
  .rk-pc-pv-tab.is-open {
    background: var(--rk-pc-pink);
    border-radius: var(--rk-pc-r-md) 0 0 var(--rk-pc-r-md);
  }
  /* C3: desktop keeps the compact edge tab but now shows a visible text
   * label (set vertically to fit the narrow tab) so the flyout isn't a
   * mystery icon. The chevron stays hidden — open/close reads from the
   * icon colour + the panel itself. */
  .rk-pc-pv-tab {
    height: auto;
    min-height: 50px;
    padding: 12px 2px;
    grid-auto-flow: row;
    align-content: center;
    gap: 8px;
    width: 46px;
  }
  .rk-pc-pv-tab__label {
    display: block;
    writing-mode: vertical-rl;
    text-orientation: mixed;
    text-align: center;
    white-space: nowrap;
    line-height: 1;
    font-size: 11.5px;
    font-weight: 600;
  }
  .rk-pc-pv-tab__chevron {
    display: none;
  }
  /* The dock is the rail's only in-flow child, so the rail is exactly
   * as tall as the dock — that's what bounds the sticky stop. */
  .rk-pc-pv-dock {
    position: relative;
    z-index: 2;
    display: block;
    width: 350px;
    max-width: 86vw;
    max-height: calc(100vh - var(--rk-pc-pv-top, 180px) - 18px);
    overflow-y: auto;
    overflow-x: hidden;
    opacity: 0;
    pointer-events: none;
    transform: translateX(calc(-100% - 120px));
  }
  /* Enabled only after first paint — see the .is-ready note above. */
  .rk-pc-pv-mount.is-ready .rk-pc-pv-dock {
    transition:
      transform 320ms cubic-bezier(0.4, 0, 0.2, 1),
      opacity 220ms ease;
  }
  .rk-pc-pv-dock.is-open {
    opacity: 1;
    pointer-events: auto;
    transform: translateX(calc(-100% - 44px));
  }
  .rk-pc-pv-dock__clip {
    overflow: visible;
    padding: 6px 8px;
  }
  .rk-pc-pv-dock .rk-pc-pv-canvas {
    margin: 0 auto;
    max-width: 100%;
  }
}

.rk-pc-pv-canvas {
  background: var(--rk-pc-card);
  border: 1px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-xl);
  box-shadow: 0 6px 20px rgba(17, 17, 17, 0.1);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
}

/* Meta strip */
.rk-pc-pv-meta {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 12px;
  color: var(--rk-pc-muted);
}
.rk-pc-pv-meta__eyebrow {
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--rk-pc-plum-soft);
}
.rk-pc-pv-meta__placement {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
  font-size: 12.5px;
  color: var(--rk-pc-ink);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rk-pc-pv-meta__placement svg {
  color: var(--rk-pc-pink);
  flex: 0 0 auto;
}
.rk-pc-pv-meta__spacer {
  flex: 1;
}
.rk-pc-pv-meta__size {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  flex: 0 0 auto;
  padding: 3px 9px;
  background: var(--rk-pc-pink-pale);
  border-radius: var(--rk-pc-r-pill);
  color: var(--rk-pc-plum);
}
.rk-pc-pv-meta__size strong {
  font: 700 13px/1 var(--rk-pc-font-display);
}
.rk-pc-pv-meta__size span {
  font-size: 10.5px;
}

/* Frame — corner + 2 rulers + stage in a 2x2 grid */
.rk-pc-pv-frame {
  display: grid;
  /* minmax(0, 1fr): without the 0 floor the stage column keeps its
   * aspect-ratio-derived min width and the frame overflows a narrow
   * container (the desktop flyout). */
  grid-template-columns: var(--rk-pc-pv-ruler, 28px) minmax(0, 1fr);
  grid-template-rows: var(--rk-pc-pv-ruler, 28px) 1fr;
  background: #faf6f0;
  border: 1px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  overflow: hidden;
  position: relative;
}
.rk-pc-pv-frame__corner {
  background: var(--rk-pc-card);
  border-right: 1px solid var(--rk-pc-hairline);
  border-bottom: 1px solid var(--rk-pc-hairline);
  display: grid;
  place-items: center;
  font: 600 9px/1 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  color: var(--rk-pc-muted);
  text-transform: uppercase;
  letter-spacing: 1px;
}
.rk-pc-pv-frame__ruler-top,
.rk-pc-pv-frame__ruler-left {
  position: relative;
  background: var(--rk-pc-card);
  overflow: hidden;
}
.rk-pc-pv-frame__ruler-top {
  border-bottom: 1px solid var(--rk-pc-hairline);
}
.rk-pc-pv-frame__ruler-left {
  border-right: 1px solid var(--rk-pc-hairline);
}

.rk-pc-pv-ruler {
  position: absolute;
  inset: 0;
}
.rk-pc-pv-tick {
  position: absolute;
}
.rk-pc-pv-tick small {
  position: absolute;
  font: 500 9px/1 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  color: var(--rk-pc-muted);
  pointer-events: none;
}
.rk-pc-pv-tick--x {
  bottom: 0;
  width: 1px;
}
.rk-pc-pv-tick--x.is-minor {
  height: 5px;
  background: rgba(17, 17, 17, 0.16);
}
.rk-pc-pv-tick--x.is-major {
  height: 13px;
  background: rgba(17, 17, 17, 0.5);
}
.rk-pc-pv-tick--x small {
  top: 2px;
  left: 3px;
}
.rk-pc-pv-tick--y {
  right: 0;
  height: 1px;
}
.rk-pc-pv-tick--y.is-minor {
  width: 5px;
  background: rgba(17, 17, 17, 0.16);
}
.rk-pc-pv-tick--y.is-major {
  width: 13px;
  background: rgba(17, 17, 17, 0.5);
}
.rk-pc-pv-tick--y small {
  right: 4px;
  top: 3px;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
}
.rk-pc-pv-ruler__hl {
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(243, 139, 185, 0.32);
  border-left: 1px solid var(--rk-pc-plum);
  border-right: 1px solid var(--rk-pc-plum);
}
.rk-pc-pv-ruler__hl-label {
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--rk-pc-plum);
  color: #fff;
  font: 700 9px/1 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  padding: 2px 5px;
  border-radius: 2px;
  white-space: nowrap;
}

/* Stage — tinted to the garment colour */
.rk-pc-pv-stage {
  position: relative;
  overflow: hidden;
  aspect-ratio: 340 / 360;
  /* min-width:0 lets the stage shrink to fit a narrow container; with no
   * min-height the aspect-ratio always holds, so the canvas grid stays
   * aligned with the mm rulers at every width. */
  min-width: 0;
}
.rk-pc-pv-stage__grid {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    var(--rk-pc-pv-dot, rgba(17, 17, 17, 0.17)) 0.6px,
    transparent 0.6px
  );
  background-size: 2.941% 2.778%;
  pointer-events: none;
}
.rk-pc-pv-stage__svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.rk-pc-pv-cross {
  stroke: var(--rk-pc-pv-cross, rgba(17, 17, 17, 0.12));
  stroke-width: 0.3;
  stroke-dasharray: 1 1.5;
}

/* Empty state */
.rk-pc-pv-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  pointer-events: none;
}
.rk-pc-pv-empty__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  width: max-content;
  max-width: min(250px, 100%);
  height: fit-content;
  /* margin:auto centres the card on BOTH axes inside the flex stage,
   * independent of the SVG / aspect-ratio sizing around it. */
  margin: auto;
  align-self: center;
  padding: 18px 20px;
  background: rgba(255, 255, 255, 0.94);
  border-radius: var(--rk-pc-r-md);
  box-shadow: 0 2px 10px rgba(17, 17, 17, 0.12);
  color: var(--rk-pc-plum);
}
.rk-pc-pv-empty__logo {
  width: 94px;
  height: auto;
  display: block;
  margin-bottom: 2px;
}
.rk-pc-pv-empty__card strong {
  font: 700 16px/1.15 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
}
.rk-pc-pv-empty__card span {
  font-size: 12.5px;
  color: var(--rk-pc-muted);
}

/* Size badge */
.rk-pc-pv-sizebadge {
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  pointer-events: none;
  max-width: 90%;
}
.rk-pc-pv-sizebadge__line {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}
.rk-pc-pv-sizebadge__cap {
  width: 1px;
  height: 10px;
  background: var(--rk-pc-plum);
  flex: 0 0 auto;
}
.rk-pc-pv-sizebadge__bar {
  height: 1.5px;
  background: var(--rk-pc-plum);
  max-width: 100%;
}
.rk-pc-pv-sizebadge__text {
  font: 500 11px/1 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  color: var(--rk-pc-plum);
  background: var(--rk-pc-card);
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid var(--rk-pc-plum);
}
.rk-pc-pv-sizebadge__text strong {
  font-weight: 700;
}

/* Placement-shape note (sleeve / hat constraints) */
.rk-pc-pv-note {
  font-size: 11.5px;
  line-height: 1.4;
  color: var(--rk-pc-plum-soft);
  background: var(--rk-pc-pink-pale);
  border-radius: var(--rk-pc-r-sm);
  padding: 7px 10px;
}

/* Legend */
/* Remove-background toggle on the preview (2026-07-03). */
.rk-pc-pv-bgtoggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 2px;
  font-size: 13px;
  color: var(--rk-pc-ink);
  cursor: pointer;
}
.rk-pc-pv-bgtoggle input {
  width: 16px;
  height: 16px;
  accent-color: var(--rk-pc-pink);
}
.rk-pc-pv-bgtoggle__busy {
  color: var(--rk-pc-muted);
  font-style: italic;
}

/* ── On-garment photo preview (W4 phase 2) ─────────────────────────────
 * The calibrated placement photo is the stage; the SVG overlay draws the
 * artwork in photo-mm space. Drag repositions only. touch-action: none
 * is scoped to the DRAGGABLE stage alone, so page scrolling (and the
 * in-cart modal's .rkc-cz__host scroll container) is never hijacked
 * anywhere else - and a read-only photo preview stays scroll-through. */
.rk-pc-pv-photo {
  position: relative;
  border-radius: var(--rk-pc-r-md);
  overflow: hidden;
  background: var(--rk-pc-hairline-soft);
  user-select: none;
  -webkit-user-select: none;
}
.rk-pc-pv-photo.is-draggable {
  touch-action: none;
  cursor: grab;
}
.rk-pc-pv-photo.is-dragging {
  cursor: grabbing;
}
.rk-pc-pv-photo__img {
  display: block;
  width: 100%;
  height: auto;
  -webkit-user-drag: none;
}
.rk-pc-pv-photo__svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
/* Calibrated-zone outline, visible only during an active drag. */
.rk-pc-pv-photo__zone {
  fill: rgba(255, 255, 255, 0.08);
  stroke: var(--rk-pc-pink);
  stroke-width: 1.2;
  stroke-dasharray: 4 3;
  vector-effect: non-scaling-stroke;
}
/* mm size badge, visible only during an active drag. */
.rk-pc-pv-photo__badge {
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  pointer-events: none;
  font: 500 11px/1 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  color: var(--rk-pc-plum);
  background: var(--rk-pc-card);
  padding: 3px 9px;
  border-radius: 3px;
  border: 1px solid var(--rk-pc-plum);
  white-space: nowrap;
}
.rk-pc-pv-photo__badge strong {
  font-weight: 700;
}
/* Broken/unreachable placement photo: keep the dock honest without
 * pretending to composite. */
.rk-pc-pv-photo__failed {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
  text-align: center;
  font-size: 12.5px;
  color: var(--rk-pc-muted);
  background: var(--rk-pc-card);
}

/* Stuck-helper / pre-order lead dialog (2026-07-03). */
.rk-pc-lead-overlay {
  /* Portalled to body: font/colour no longer inherit from .rk-pc-root. */
  font-family: var(--rk-pc-font-body);
  color: var(--rk-pc-ink);
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  background: rgba(20, 12, 20, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.rk-pc-lead {
  position: relative;
  width: 100%;
  max-width: 400px;
  background: #fff;
  border: 2px solid var(--rk-pc-ink);
  border-radius: 16px;
  padding: 22px 20px 20px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.3);
  font-family: var(--rk-pc-font-body, inherit);
}
.rk-pc-lead__close {
  position: absolute;
  top: 8px;
  right: 10px;
  border: 0;
  background: none;
  font-size: 26px;
  line-height: 1;
  color: var(--rk-pc-muted);
  cursor: pointer;
}
.rk-pc-lead__title {
  margin: 0 0 6px;
  font: 800 19px/1.15 var(--rk-pc-font-display);
}
.rk-pc-lead__blurb {
  margin: 0 0 14px;
  font-size: 13.5px;
  line-height: 1.45;
  color: var(--rk-pc-muted);
}
.rk-pc-lead__field {
  display: block;
  margin-bottom: 10px;
}
.rk-pc-lead__label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 4px;
}
.rk-pc-lead__field input,
.rk-pc-lead__field textarea {
  width: 100%;
  box-sizing: border-box;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  padding: 10px 12px;
  font: inherit;
  font-size: 14px;
}
.rk-pc-lead__field textarea {
  resize: vertical;
}
.rk-pc-lead__optin {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 12.5px;
  color: var(--rk-pc-muted);
  margin: 4px 0 14px;
  cursor: pointer;
}
.rk-pc-lead__optin input {
  margin-top: 2px;
  width: 15px;
  height: 15px;
  accent-color: var(--rk-pc-pink);
  flex: 0 0 auto;
}
.rk-pc-lead__err {
  font-size: 12.5px;
  color: #9f1239;
  margin-bottom: 10px;
}
.rk-pc-lead__btn {
  width: 100%;
  background: var(--rk-pc-pink);
  color: var(--rk-pc-ink);
  border: 2px solid var(--rk-pc-ink);
  border-radius: 999px;
  padding: 13px 18px;
  font: 700 15px/1 var(--rk-pc-font-display);
  cursor: pointer;
}
.rk-pc-lead__btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.rk-pc-lead__done {
  text-align: center;
  padding: 8px 4px;
}
.rk-pc-lead__done-tick {
  width: 46px;
  height: 46px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: #1a7f37;
  color: #fff;
  font-size: 26px;
  line-height: 46px;
}

.rk-pc-pv-legend {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11.5px;
  color: var(--rk-pc-muted);
}
.rk-pc-pv-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.rk-pc-pv-legend__item--muted {
  color: var(--rk-pc-muted);
}
.rk-pc-pv-legend__spacer {
  flex: 1;
}
.rk-pc-pv-legend__swatch {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 1px solid var(--rk-pc-hairline);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
  flex: 0 0 auto;
}

/* "Removing background…" working badge inside the preview stage. */
.rk-pc-pv-working {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 11px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-pill);
  font-size: 11.5px;
  font-weight: 600;
  color: var(--rk-pc-plum);
  box-shadow: 0 2px 8px rgba(17, 17, 17, 0.14);
  pointer-events: none;
  z-index: 4;
}
.rk-pc-pv-working__spinner {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid var(--rk-pc-pink-pale);
  border-top-color: var(--rk-pc-plum);
  animation: rk-pc-pv-spin 0.8s linear infinite;
}
@keyframes rk-pc-pv-spin {
  to {
    transform: rotate(360deg);
  }
}

/* "Remove the background" opt-in on the upload step. */
.rk-pc-bg-toggle {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 13px;
  border: 1px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  background: var(--rk-pc-hairline-soft);
  cursor: pointer;
}
.rk-pc-bg-toggle input {
  width: 17px;
  height: 17px;
  margin: 1px 0 0;
  flex: 0 0 auto;
  accent-color: var(--rk-pc-plum);
  cursor: pointer;
}
.rk-pc-bg-toggle__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.rk-pc-bg-toggle__title {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--rk-pc-ink);
}
.rk-pc-bg-toggle__hint {
  font-size: 11.5px;
  line-height: 1.4;
  color: var(--rk-pc-muted);
}

/* ─── Phase 74: bundle preview strip ────────────────────────────────
 * Rendered above the cart CTA when the matched template has bundle
 * members wired. Atelier-aligned: cream card, plum accent on the icon,
 * subtle hairline border, modest type. Sits flush with the price
 * breakdown / total row so the customer sees what's in the bundle
 * right before they hit Add to cart. */
.rk-pc-bundle-preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  margin: 0 0 10px;
  background: var(--rk-pc-card);
  border: 1px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-lg);
  box-shadow: var(--rk-pc-sh-card);
}
.rk-pc-bundle-preview__head {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.rk-pc-bundle-preview__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: var(--rk-pc-r-pill);
  background: var(--rk-pc-amber);
  color: var(--rk-pc-amber-ink);
  font-size: 14px;
  line-height: 1;
}
.rk-pc-bundle-preview__title {
  font-family: var(--rk-pc-font-display);
  font-weight: 700;
  font-size: 14px;
  color: var(--rk-pc-ink);
  letter-spacing: 0.01em;
}
.rk-pc-bundle-preview__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.rk-pc-bundle-preview__item {
  font-size: 13.5px;
  line-height: 1.45;
  color: var(--rk-pc-ink-2);
  display: flex;
  align-items: center;
  gap: 8px;
  /* Design pass: 12px padding + 44px min-height to meet the WCAG/Apple
     touch-target minimum (these rows are the primary tap control). */
  padding: 12px;
  min-height: 44px;
  border: 1px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  background: var(--rk-pc-hairline-soft);
  transition: background 120ms ease, border-color 120ms ease;
}
/* product thumbnail on bundle rows - visual product, sold visually */
.rk-pc-bundle-preview__thumb {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  margin-right: 2px;
}
.rk-pc-bundle-preview__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.rk-pc-bundle-preview__item.is-mandatory {
  background: var(--rk-pc-hairline-soft);
}
.rk-pc-bundle-preview__item.is-optional {
  cursor: pointer;
  background: var(--rk-pc-card);
}
.rk-pc-bundle-preview__item.is-optional:hover {
  border-color: var(--rk-pc-plum-soft);
}
/* Design pass: selected = plum family, NOT amber. Amber is reserved
   for savings/money (the discount strips + the -NN% pill), so a
   selected add-on no longer reads as "discounted." */
.rk-pc-bundle-preview__item.is-optional.is-selected {
  border-color: var(--rk-pc-plum-soft);
  background: var(--rk-pc-plum-pale);
  color: var(--rk-pc-plum);
}
.rk-pc-bundle-preview__toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  cursor: pointer;
  user-select: none;
}
.rk-pc-bundle-preview__toggle input[type="checkbox"] {
  /* Visually hidden but still focusable for keyboard users. */
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
}
.rk-pc-bundle-preview__checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1.5px solid var(--rk-pc-ink-2);
  border-radius: var(--rk-pc-r-sm);
  background: var(--rk-pc-card);
  color: var(--rk-pc-ink);
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
}
.rk-pc-bundle-preview__item.is-selected .rk-pc-bundle-preview__checkbox {
  background: var(--rk-pc-ink);
  border-color: var(--rk-pc-ink);
  color: var(--rk-pc-amber);
}
.rk-pc-bundle-preview__mandatory {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: var(--rk-pc-r-pill);
  background: var(--rk-pc-mint-soft);
  color: var(--rk-pc-forest);
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
}
.rk-pc-bundle-preview__qty {
  font-weight: 700;
  color: var(--rk-pc-ink);
}
.rk-pc-bundle-preview__item.is-selected .rk-pc-bundle-preview__qty {
  color: var(--rk-pc-amber-ink);
}
.rk-pc-bundle-preview__name {
  flex: 1;
}
.rk-pc-bundle-preview__plain {
  color: var(--rk-pc-muted);
  font-style: italic;
}
.rk-pc-bundle-preview__price {
  font-weight: 700;
  color: var(--rk-pc-ink);
  margin-left: auto;
  white-space: nowrap;
}
.rk-pc-bundle-preview__item.is-selected .rk-pc-bundle-preview__price {
  color: var(--rk-pc-plum);
}
.rk-pc-bundle-preview__hint {
  margin: 2px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--rk-pc-muted);
}

/* Phase 80: pet multi-buy discount display. Brand palette: warm cream
   #F1C672 background, plum #7E3877 text. Strike-through "was" + bold
   "now" + a small pill on the row, an explainer strip above the
   savings strip. */
.rk-pc-bundle-preview__price--discounted {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.rk-pc-bundle-preview__price-was {
  text-decoration: line-through;
  text-decoration-thickness: 1px;
  color: var(--rk-pc-muted);
  font-weight: 500;
  font-size: 0.85em;
}
.rk-pc-bundle-preview__price-now {
  color: #7E3877;
  font-weight: 700;
}
.rk-pc-bundle-preview__save-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 1px 7px;
  border-radius: 999px;
  background: #F1C672;
  color: #7E3877;
  white-space: nowrap;
}
.rk-pc-pet-savings {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 0;
  padding: 8px 12px;
  border-radius: 6px;
  background: #F1C672;
  border: 1px solid rgba(126, 56, 119, 0.25);
  font-size: 13px;
  line-height: 1.4;
  color: #7E3877;
}
.rk-pc-pet-savings--explainer {
  background: rgba(241, 198, 114, 0.35);
}
.rk-pc-pet-savings__icon {
  font-size: 14px;
  color: #7E3877;
}
.rk-pc-pet-savings__label {
  color: #7E3877;
}
.rk-pc-pet-savings__label strong {
  font-weight: 700;
  color: #7E3877;
}

/* Phase 83: the bundle multi-buy teaser now lives INSIDE the unified
   perks banner (.rk-pc-promo-banner__row) so offers never stack as two
   separate strips. The old standalone .rk-pc-bundle-teaser rules were
   removed here. */

/* Phase 81: bundle row inline variant picker. Renders below the row
   label when ticked + the member has more than one variant. Atelier
   palette - cream tile background, ink text, amber border on
   picked tile. */
.rk-pc-bundle-preview__item.is-optional {
  flex-wrap: wrap;
}
.rk-pc-bundle-preview__pick-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 6px 0 0;
  padding: 4px 0 0 28px; /* line up under the row label, past the checkbox */
  font-size: 13px;
  color: var(--rk-pc-muted);
}
.rk-pc-bundle-preview__pick-summary.is-empty {
  color: var(--rk-pc-amber-ink);
  font-weight: 600;
}
.rk-pc-bundle-preview__pick-label {
  font-weight: 600;
  color: var(--rk-pc-ink);
}
.rk-pc-bundle-preview__pick-change {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: var(--rk-pc-amber-ink);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.rk-pc-bundle-preview__pick-body {
  width: 100%;
  margin: 8px 0 0;
  padding: 12px;
  background: rgba(0, 0, 0, 0.025);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.rk-pc-bundle-preview__axis {
  margin-bottom: 10px;
}
.rk-pc-bundle-preview__axis:last-child {
  margin-bottom: 0;
}
.rk-pc-bundle-preview__axis-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--rk-pc-muted);
  margin: 0 0 6px;
}
.rk-pc-bundle-preview__axis-picked {
  font-weight: 600;
  color: var(--rk-pc-ink);
  text-transform: none;
  letter-spacing: 0;
}
.rk-pc-bundle-preview__axis-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.rk-pc-bundle-preview__axis-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  /* Design pass: 44px min-height + roomier padding for a proper
     touch target (was 6px/12px ~32px tall). */
  min-height: 44px;
  padding: 8px 14px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  color: var(--rk-pc-ink);
  transition: border-color 120ms ease, background 120ms ease,
    color 120ms ease;
}
.rk-pc-bundle-preview__axis-chip:hover:not(.is-disabled):not(.is-active) {
  border-color: var(--rk-pc-plum-soft);
}
/* Design pass: active variant = plum, matching the selected-row
   treatment. Amber stays reserved for savings/money. */
.rk-pc-bundle-preview__axis-chip.is-active {
  border-color: var(--rk-pc-plum);
  background: var(--rk-pc-plum);
  color: #fff;
  font-weight: 600;
}
.rk-pc-bundle-preview__axis-chip.is-disabled {
  opacity: 0.4;
  cursor: not-allowed;
  text-decoration: line-through;
}
.rk-pc-bundle-preview__axis-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 10px 0 0;
}

/* Phase 81c: blocker strip - sits above the ATC button when a bundle
   add-on still needs a variant pick. Dashed amber border to match
   the atelier palette, NOT alarmist red. */
.rk-pc-bundle-blocker {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 0;
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(241, 198, 114, 0.18);
  border: 1px dashed var(--rk-pc-amber-ink);
  font-size: 13px;
  line-height: 1.4;
  color: var(--rk-pc-ink);
}
.rk-pc-bundle-blocker__icon {
  font-size: 14px;
  color: var(--rk-pc-amber-ink);
}
.rk-pc-bundle-blocker__label strong {
  color: var(--rk-pc-amber-ink);
  font-weight: 700;
}

/* ─── Custom fields (Phase 72/83 admin-defined questions) ─────────────── */

.rk-pc-custom-fields {
  padding: 14px 16px;
}
.rk-pc-custom-fields__list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.rk-pc-field__label {
  display: block;
  font: 700 13px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-ink);
  margin-bottom: 6px;
}
.rk-pc-field__required {
  color: var(--rk-pc-plum);
}
.rk-pc-field__hint {
  margin: 0 0 6px;
  font-size: 12px;
  color: var(--rk-pc-muted);
}
.rk-pc-field__text,
.rk-pc-field__textarea,
.rk-pc-field__select,
.rk-pc-field__date {
  width: 100%;
  padding: 10px 12px;
  background: var(--rk-pc-card);
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  font: 14px/1.45 var(--rk-pc-font-body);
  color: var(--rk-pc-ink);
  box-sizing: border-box;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.rk-pc-field__textarea {
  resize: vertical;
  min-height: 72px;
}
.rk-pc-field__text:focus,
.rk-pc-field__textarea:focus,
.rk-pc-field__select:focus,
.rk-pc-field__date:focus {
  outline: 0;
  border-color: var(--rk-pc-pink);
  box-shadow: 0 0 0 3px var(--rk-pc-pink-pale);
}

/* "+$X" chip beside options carrying a surcharge. */
.rk-pc-field__opt-price {
  font: 600 11px/1 var(--rk-pc-font-body);
  color: var(--rk-pc-plum);
  background: var(--rk-pc-plum-pale);
  border-radius: var(--rk-pc-r-pill);
  padding: 3px 7px;
  white-space: nowrap;
}

/* Radio + checkbox groups share a stacked choice-row treatment. */
.rk-pc-field__radio-group,
.rk-pc-field__checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.rk-pc-field__choice-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  background: var(--rk-pc-card);
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
}
.rk-pc-field__choice-row:hover {
  border-color: var(--rk-pc-pink-soft);
}
.rk-pc-field__choice-row.is-selected {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
}
.rk-pc-field__choice-input {
  accent-color: var(--rk-pc-plum);
  margin: 0;
  flex: 0 0 auto;
}
.rk-pc-field__choice-label {
  flex: 1 1 auto;
  font: 14px/1.3 var(--rk-pc-font-body);
  color: var(--rk-pc-ink);
}

/* Hex swatch grid: round dots (or image thumbs) with the label below. */
.rk-pc-field__swatch-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.rk-pc-field__swatch {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border: 1.5px solid transparent;
  border-radius: var(--rk-pc-r-md);
  background: none;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
}
.rk-pc-field__swatch:hover {
  border-color: var(--rk-pc-pink-soft);
}
.rk-pc-field__swatch.is-selected {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
}
.rk-pc-field__swatch-dot {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1.5px solid var(--rk-pc-hairline);
  display: block;
}
.rk-pc-field__swatch-img {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1.5px solid var(--rk-pc-hairline);
  object-fit: cover;
  display: block;
}
.rk-pc-field__swatch-label {
  font: 11px/1.2 var(--rk-pc-font-body);
  color: var(--rk-pc-ink-2);
  max-width: 72px;
  text-align: center;
}

/* Image swatch tiles (styled here alongside the Phase 83 types; the
 * markup shipped in Phase 72). */
.rk-pc-field__image-swatch-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.rk-pc-field__image-swatch-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px;
  border: 1.5px solid var(--rk-pc-hairline);
  border-radius: var(--rk-pc-r-md);
  background: var(--rk-pc-card);
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
}
.rk-pc-field__image-swatch-tile:hover {
  border-color: var(--rk-pc-pink-soft);
}
.rk-pc-field__image-swatch-tile.is-selected {
  border-color: var(--rk-pc-pink);
  background: var(--rk-pc-pink-pale);
}
.rk-pc-field__image-swatch-img {
  width: 56px;
  height: 56px;
  border-radius: var(--rk-pc-r-sm);
  object-fit: cover;
  display: block;
}
.rk-pc-field__image-swatch-placeholder {
  width: 56px;
  height: 56px;
  border-radius: var(--rk-pc-r-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--rk-pc-hairline-soft);
  font: 700 14px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-muted);
}
.rk-pc-field__image-swatch-label {
  font: 11px/1.2 var(--rk-pc-font-body);
  color: var(--rk-pc-ink-2);
  max-width: 84px;
  text-align: center;
}
.rk-pc-field__image-swatch-price {
  font: 600 11px/1 var(--rk-pc-font-body);
  color: var(--rk-pc-plum);
  background: var(--rk-pc-plum-pale);
  border-radius: var(--rk-pc-r-pill);
  padding: 3px 7px;
}

/* C4: iOS Safari zooms the page in whenever a focused text field renders
 * below 16px. On phones bump every rk-pc free-text field to 16px so
 * focusing an input never triggers that jarring auto-zoom. Placed last so
 * it wins over the base rules by source order at equal specificity. */
@media (max-width: 640px) {
  .rk-pc-text-input,
  .rk-pc-savedesign__input,
  .rk-pc-sleeve-name__input,
  .rk-pc-lead__field input,
  .rk-pc-lead__field textarea,
  .rk-pc-field__text,
  .rk-pc-field__textarea,
  /* 2026-07-24: four fields the C4 sweep missed, found during the iPhone
   * scroll audit. Each was under 16px (or `font: inherit` under a sub-16px
   * theme body), so focusing it zoomed the whole page and the zoom PERSISTS
   * after blur, which then makes every later layout look scrolled-wrong. */
  .rk-pc-notes-input,
  .rk-pc-field__select,
  .rk-pc-field__date,
  .rk-pc-past-order__input {
    font-size: 16px;
  }

  /* ── 2026-07-24 mobile ergonomics (44pt tap-target sweep) ──────────────
   * Apple's HIG minimum is 44x44pt; the audit measured six controls below
   * it. Scoped to phones so desktop layouts do not shift. Values stay on
   * the 4px grid. */
  /* Thread dots were 34px in a 9px-gap grid of 26: a dense mis-tap field
   * for exactly the choice that decides what gets stitched. */
  .rk-pc-thread-dot {
    width: 44px;
    height: 44px;
  }
  /* The mandatory per-step confirm was ~39px tall; the sticky bar's own
   * button ~37px; the stage CTA ~41px. All are primary path controls. */
  .rk-pc-substep-confirm,
  .rk-pc-mobile-bar__btn,
  .rk-pc-add-spot-btn {
    min-height: 44px;
  }
  /* Size slider: a drag control on paid-tier boundaries had a 22px thumb
   * in a 28px lane. Thumb to 28px inside a 44px lane. */
  .rk-pc-slider__wrap {
    height: 44px;
  }
  .rk-pc-slider__input::-webkit-slider-thumb {
    width: 28px;
    height: 28px;
  }
  .rk-pc-slider__input::-moz-range-thumb {
    width: 28px;
    height: 28px;
  }
  /* The thread-chip remove X keeps its 18px look but gains a 44px hit
   * area via padding + negative margin, so removing a colour is not a
   * precision task. */
  .rk-pc-thread-chip__x {
    box-sizing: content-box;
    padding: 13px;
    margin: -13px;
    background-clip: content-box;
  }

  /* The fixed bottom bar covers the last ~90-125px of the flow while a
   * design is in progress (its render condition is mirrored onto the root
   * as data-rk-bar). Give the flow that space back so the real add-to-cart
   * is never buried underneath the bar. */
  .rk-pc-root[data-rk-bar="1"] {
    padding-bottom: 132px;
  }
}

/* Surviving programmatic scrolls (desktop follow-scrolls, the off-screen
 * next-stage nudge) must never land a target underneath the theme's sticky
 * header. Static, motion-free, applies at every width. */
.rk-pc-stage,
.rk-pc-substep,
.rk-pc-field,
.rk-pc-editor-wrap,
.rk-pc-sec {
  scroll-margin-top: 96px;
}

/* ─── Garment stages (multi-garment templates) ────────────────────────── */
/* One titled block per garment, so a bundle reads hoodie -> cap -> socks
   instead of one flat list of spots. Rendered only when stages.ts has an
   entry for the template tag; every single-garment template is untouched. */

.rk-pc-stages {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.rk-pc-stage {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border: 1.5px solid var(--rk-pc-plum-pale);
  border-radius: var(--rk-pc-r-md);
  background: #fff;
}
/* A finished stage recedes so the eye lands on what is still outstanding. */
.rk-pc-stage[data-done="1"] {
  border-color: var(--rk-pc-mint-soft);
  background: #fbfffa;
}
/* 2026-07-24: the NEXT unfinished stage leans forward instead. Static
 * accent only, no animation: after a save the eye should find the next
 * garment without the page being scrolled for them (the old post-save
 * auto-scroll was a core part of the iPhone jank complaint). */
.rk-pc-stage[data-next="1"] {
  border-color: var(--rk-pc-pink);
  box-shadow: 0 1px 6px rgba(214, 31, 105, 0.09);
}
.rk-pc-stage[data-next="1"] .rk-pc-stage__state {
  color: #fff;
  background: var(--rk-pc-pink);
}

.rk-pc-stage__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.rk-pc-stage__title {
  font: 800 15px/1.2 var(--rk-pc-font-display);
  color: var(--rk-pc-plum);
}

.rk-pc-stage__state {
  font: 700 11px/1 var(--rk-pc-font-display);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 4px 8px;
  border-radius: 999px;
  color: var(--rk-pc-muted);
  background: #f3f4f6;
}
.rk-pc-stage[data-done="1"] .rk-pc-stage__state {
  color: #2f6a4a;
  background: var(--rk-pc-mint-soft);
}

.rk-pc-stage__hint {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--rk-pc-ink-2);
}

.rk-pc-stage .rk-pc-add-spot-btn {
  align-self: flex-start;
}

/* ─── EST. year entry (cap back) ──────────────────────────────────────── */

.rk-pc-year {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rk-pc-year__prefix {
  font: 800 22px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-plum);
  letter-spacing: 0.02em;
}

.rk-pc-year__box {
  box-sizing: border-box;
  width: 52px;
  height: 62px;
  text-align: center;
  /* line-height 1 with zero padding clipped the digits against the theme's
     inherited input styles. Explicit padding + normal leading centres them. */
  font-family: var(--rk-pc-font-display);
  font-weight: 800;
  font-size: 26px;
  line-height: 1.2;
  padding: 6px 4px;
  color: var(--rk-pc-ink);
  border: 2px solid var(--rk-pc-pink);
  border-radius: var(--rk-pc-r-md);
  background: #fff;
}
.rk-pc-year__box:focus {
  outline: none;
  border-color: var(--rk-pc-plum);
  box-shadow: 0 0 0 3px var(--rk-pc-pink-soft);
}

@media (max-width: 420px) {
  .rk-pc-year__box { width: 40px; height: 50px; font-size: 21px; }
  .rk-pc-year__prefix { font-size: 19px; }
}

.rk-pc-year-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}
.rk-pc-year__above {
  font: 800 20px/1 var(--rk-pc-font-display);
  color: var(--rk-pc-plum);
  letter-spacing: 0.04em;
}
